/**
 * LYT Communications - Project Map Page
 * Version: 4.0.0
 * Created: 2026-02-02
 * Updated: 2026-02-04
 * Route: #project-map
 *
 * Interactive map-based project management view using Leaflet/OpenStreetMap.
 * Displays construction segments as polylines with color-coded status,
 * handhole markers, flowerpot markers, and splice point markers with PM status.
 *
 * Features:
 * - Leaflet map with satellite/street toggle (no API key needed)
 * - Color-coded polyline segments (boring/pulling/splicing status)
 * - Clickable handhole markers (sized by type)
 * - Clickable flowerpot markers (utility boxes - UG12)
 * - Clickable splice point markers with PM status indicators
 * - Segment detail side panel with workflow tabs
 * - Splice point detail panel with PM readings
 * - Section/status/phase filters
 * - List view fallback
 * - Photo upload to Google Drive
 * - Auto-refresh polling (30s)
 * - Toast notifications for actions
 * - Mobile-optimized with bottom sheet details
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { ArrowLeft, Map, List, Search, AlertCircle, MapPin, Ruler, Users, Zap, X, ChevronDown, ChevronUp, Filter, Camera, CheckCircle, Clock, AlertTriangle, Navigation, Wrench, Cable, Flower2, Activity, Radio, Eye, HardHat, Shield, DollarSign, Save, FileText, Upload, RefreshCw, Menu } from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { STATUS_COLORS, STATUS_COLORS_CB, BILLING_STATUS_COLORS, BILLING_STATUS_COLORS_CB, PHOTO_REQUIREMENTS, PM_THRESHOLDS, PM_STATUS, evaluatePMReading, GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBS, DEFAULT_MAP_PRESETS } from '../config/mapConfig';
import BoringTracker from '../components/Workflow/BoringTracker';
import PullingTracker from '../components/Workflow/PullingTracker';
import SplicingTracker from '../components/Workflow/SplicingTracker';
import FlowerpotInstallTracker from '../components/Workflow/FlowerpotInstallTracker';
import LocateTracker from '../components/Workflow/LocateTracker';
import DamageReport from '../components/Workflow/DamageReport';
import RestorationTracker from '../components/Workflow/RestorationTracker';
// eslint-disable-next-line no-unused-vars
import { loadFullProject, loadFullProjectWithLineItems, loadProjectsList, loadSections, isDemoMode, updateSegmentField, updateSpliceField, updateDeviceField, logAction, loadLocates, createLocate, verifyLocate, loadDamageReports, createDamageReport, loadEmailInvoices, scanDriveInvoices, uploadInvoicePDF } from '../services/mapService';
import { uploadPhotoBatch } from '../services/photoUploadService';
// import CrewTracker from '../components/Map/CrewTracker'; // GPS moved to Settings
// eslint-disable-next-line no-unused-vars
import Toast, { useToast } from '../components/Toast';
// eslint-disable-next-line no-unused-vars
import { GoogleMap, useJsApiLoader, Polyline, Marker, DrawingManager } from '@react-google-maps/api';
import { bulkAssignSegments, loadMapNotes, loadMapPresets, saveMapPreset, fetchFullRates } from '../services/mapService';
import MapNotesLayer from '../components/Map/MapNotesLayer';
import MyLocationMarker, { GpsToggleButton } from '../components/Map/MyLocationMarker';
import CrewLocationLayer from '../components/Map/CrewLocationLayer';

// Map state persistence
const MAP_STATE_KEY = 'lyt-project-map-state';
function loadMapState() {
  try { return JSON.parse(localStorage.getItem(MAP_STATE_KEY)) || {}; } catch { return {}; }
}

// Helper: get status color (cb = colorblind mode)
function getStatusColor(status, cb) {
  const C = cb ? STATUS_COLORS_CB : STATUS_COLORS;
  switch (status) {
    case 'QC Approved': return C.QC_APPROVED;
    case 'Complete': return C.COMPLETE;
    case 'In Progress': return C.IN_PROGRESS;
    case 'Issue': return C.ISSUE;
    case 'Blocked': return C.BLOCKED;
    default: return C.NOT_STARTED;
  }
}

// Helper: get status icon
function StatusIcon({ status, size, cb }) {
  const s = size || 14;
  const C = cb ? STATUS_COLORS_CB : STATUS_COLORS;
  switch (status) {
    case 'QC Approved': return <CheckCircle size={s} color={C.QC_APPROVED} />;
    case 'Complete': return <CheckCircle size={s} color={C.COMPLETE} />;
    case 'In Progress': return <Clock size={s} color={C.IN_PROGRESS} />;
    case 'Issue': return <AlertTriangle size={s} color={C.ISSUE} />;
    default: return <Clock size={s} color={C.NOT_STARTED} />;
  }
}

// ===== GOOGLE MAPS COMPONENT =====
const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 30.0049, lng: -97.1608 }; // Smithville TX
function ProjectGoogleMap({ segments, splicePoints, handholes, flowerpots, pedestals, cabinets, fats, selectedSegment, selectedSplice, onSelectSegment, onSelectSplice, onSelectHandhole, onSelectFlowerpot, onSelectPedestal, onSelectCabinet, filterPhase, darkMode, showFlowerpots, showSplicePoints, showHandholes, showPedestals, showCabinets, showAerial, showFats, adminMode, adminSelectedSegmentIds, adminSelectedDeviceIds, projectId, drawRectMode, onRectangleSelect, searchMatchIds, colorblindMode, viewMode, mapNotes, showNotes, isAddingNote, onAddNoteToggle, onNotesChanged, loggedInUser, section, allDevices, isAdmin, isMobile, gpsOn, onGpsToggle }) {
  const [map, setMap] = useState(null);
  const [mapType, setMapType] = useState(() => {
    try { return localStorage.getItem('lyt-map-type') || 'roadmap'; } catch { return 'roadmap'; }
  });
  const [mapZoom, setMapZoom] = useState(16);
  const [legendCollapsed, setLegendCollapsed] = useState(true);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBS,
  });

  // Calculate bounds from all points
  const bounds = useMemo(() => {
    if (!segments || segments.length === 0) return null;
    const points = [];
    segments.forEach(seg => {
      if (seg.gps_start_lat && seg.gps_start_lng) points.push({ lat: parseFloat(seg.gps_start_lat), lng: parseFloat(seg.gps_start_lng) });
      if (seg.gps_end_lat && seg.gps_end_lng) points.push({ lat: parseFloat(seg.gps_end_lat), lng: parseFloat(seg.gps_end_lng) });
    });
    if (showFlowerpots && flowerpots) {
      flowerpots.forEach(fp => {
        if (fp.gps_lat && fp.gps_lng) points.push({ lat: parseFloat(fp.gps_lat), lng: parseFloat(fp.gps_lng) });
      });
    }
    if (showSplicePoints && splicePoints) {
      splicePoints.forEach(sp => {
        if (sp.gps_lat && sp.gps_lng) points.push({ lat: parseFloat(sp.gps_lat), lng: parseFloat(sp.gps_lng) });
      });
    }
    return points;
  }, [segments, splicePoints, flowerpots, showFlowerpots, showSplicePoints]);

  // Fit bounds when map loads
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    if (bounds && bounds.length > 0 && window.google) {
      const googleBounds = new window.google.maps.LatLngBounds();
      bounds.forEach(p => googleBounds.extend(p));
      mapInstance.fitBounds(googleBounds, 50);
    }
  }, [bounds]);

  // Auto-center map when project changes OR when bounds first arrive after initial load
  const fittedProjectId = useRef(null);
  useEffect(() => {
    if (map && bounds && bounds.length > 0 && window.google && fittedProjectId.current !== projectId) {
      const googleBounds = new window.google.maps.LatLngBounds();
      bounds.forEach(p => googleBounds.extend(p));
      map.fitBounds(googleBounds, 50);
      fittedProjectId.current = projectId;
    }
  }, [projectId, map, bounds]);

  // Use handholes from props (loaded from DB with unique IDs)
  // Fall back to deriving from segments if no handholes provided
  const handholesData = useMemo(() => {
    if (handholes && handholes.length > 0) {
      return handholes.map(hh => ({
        device_id: hh.device_id || hh.handhole_id,
        id: hh.id || hh.handhole_id,
        lat: parseFloat(hh.gps_lat),
        lng: parseFloat(hh.gps_lng),
        label: hh.label || hh.id || hh.handhole_id,
        type: hh.type || '',
        code: hh.code || 'UG20',
      })).filter(hh => !isNaN(hh.lat) && !isNaN(hh.lng));
    }
    // Fallback: derive from segments (use native Map, not lucide-react Map icon)
    // eslint-disable-next-line no-undef
    const NativeMap = typeof window !== 'undefined' ? window.Map : (typeof globalThis !== 'undefined' ? globalThis.Map : Map);
    const hh = new NativeMap();
    segments.forEach(seg => {
      const fromLabel = seg.from_handhole || seg.contractor_id?.split('→')[0] || '?';
      const toLabel = seg.to_handhole || seg.contractor_id?.split('→')[1] || '?';
      const startLat = parseFloat(seg.gps_start_lat);
      const startLng = parseFloat(seg.gps_start_lng);
      const endLat = parseFloat(seg.gps_end_lat);
      const endLng = parseFloat(seg.gps_end_lng);

      if (!isNaN(startLat) && !isNaN(startLng) && (fromLabel.includes('x') || fromLabel.match(/^[A-Z]\d*$/))) {
        const key = `${startLat},${startLng}`;
        if (!hh.has(key)) hh.set(key, { lat: startLat, lng: startLng, label: fromLabel, type: '', code: '' });
      }
      if (!isNaN(endLat) && !isNaN(endLng) && (toLabel.includes('x') || toLabel.match(/^[A-Z]\d*$/))) {
        const key = `${endLat},${endLng}`;
        if (!hh.has(key)) hh.set(key, { lat: endLat, lng: endLng, label: toLabel, type: '', code: '' });
      }
    });
    return Array.from(hh.values());
  }, [handholes, segments]);

  // snappedPaths removed — O(segments × devices) computation was 244M+ operations
  // at 14K segments × 8K devices, freezing the browser. Raw GPS is accurate enough.

  // Get status color (aerial segments always steel blue)
  const C = colorblindMode ? STATUS_COLORS_CB : STATUS_COLORS;

  // Vitruvi status colors
  const VITRUVI_COLORS = {
    issued: '#9E9E9E',     // gray — issued but not started
    assigned: '#FF9800',   // orange — assigned to crew
    completed: '#4CAF50',  // green — work done
    verified: '#2196F3',   // blue — QC verified
    closed: '#9C27B0',     // purple — fully closed/billed
  };

  const getSegmentColor = (seg) => {
    if (seg.bore_code === 'aerial') return '#4682B4';
    if (viewMode === 'vitruvi') {
      return VITRUVI_COLORS[seg.vitruvi_status] || '#616161';
    }
    const status = filterPhase === 'pulling' ? seg.pulling_status : filterPhase === 'splicing' ? (seg.splicing_status || 'Not Started') : seg.boring_status;
    if (status === 'QC Approved' || status === 'Complete') return C.COMPLETE;
    if (status === 'In Progress') return C.IN_PROGRESS;
    if (status === 'Issue') return C.ISSUE;
    if (status === 'Blocked') return C.BLOCKED;
    return C.NOT_STARTED;
  };

  // Get PM status color for splice points
  const getPMStatusColor = (sp) => {
    if (sp.splice_type !== '1x4' || !sp.pm_readings) return '#9E9E9E';
    try {
      const readings = typeof sp.pm_readings === 'string' ? JSON.parse(sp.pm_readings) : sp.pm_readings;
      if (!Array.isArray(readings)) return '#9E9E9E';
      if (readings.some(r => r.status === 'fail')) return '#F44336';
      if (readings.some(r => r.status === 'warning')) return '#FF9800';
      if (readings.every(r => r.status === 'pass')) return '#4CAF50';
    } catch (e) { /* ignore */ }
    return '#9E9E9E';
  };

  if (loadError) return <div style={{ padding: 20, color: '#f44336' }}>Error loading Google Maps</div>;
  if (!isLoaded) return <div style={{ padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Loading map...</div>;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={map ? undefined : defaultCenter}
        zoom={map ? undefined : 15}
        onLoad={onLoad}
        onZoomChanged={() => { if (map) setMapZoom(map.getZoom()); }}
        onClick={(e) => { if (isAddingNote && MapNotesLayer.handleMapClick) MapNotesLayer.handleMapClick(e); }}
        mapTypeId={mapType}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
          styles: darkMode ? [
            { elementType: 'geometry', stylers: [{ color: '#212121' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          ] : [
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          ],
        }}
      >
        {/* Segment Polylines - only render after map is ready to avoid setAt error */}
        {map && segments.map(seg => {
          if (!showAerial && seg.bore_code === 'aerial') return null;
          const startLat = parseFloat(seg.gps_start_lat);
          const startLng = parseFloat(seg.gps_start_lng);
          const endLat = parseFloat(seg.gps_end_lat);
          const endLng = parseFloat(seg.gps_end_lng);
          if (isNaN(startLat) || isNaN(startLng) || isNaN(endLat) || isNaN(endLng)) return null;

          const isSelected = selectedSegment && (selectedSegment.segment_id === seg.segment_id || selectedSegment === seg.segment_id);
          const isAdminSelected = adminMode && adminSelectedSegmentIds && adminSelectedSegmentIds.has(seg.segment_id);
          // Use path_coordinates if available, otherwise raw GPS start→end
          const segPath = seg.path_coordinates || [{ lat: startLat, lng: startLng }, { lat: endLat, lng: endLng }];
          const isAerial = seg.bore_code === 'aerial';
          const isSearchDimmed = searchMatchIds && !searchMatchIds.has(seg.segment_id);
          return (
            <React.Fragment key={seg.segment_id}>
              {/* Gold highlight for admin-selected segments */}
              {isAdminSelected && (
                <Polyline
                  path={segPath}
                  options={{
                    strokeColor: '#FFD700',
                    strokeWeight: 12,
                    strokeOpacity: 0.6,
                    clickable: false,
                    zIndex: 3,
                  }}
                />
              )}
              {/* Visible segment line — dashed for aerial, solid for all others */}
              <Polyline
                path={segPath}
                options={{
                  strokeColor: getSegmentColor(seg),
                  strokeWeight: isSearchDimmed ? 2 : (isSelected || isAdminSelected ? 8 : 5),
                  strokeOpacity: isAerial ? 0 : (isSearchDimmed ? 0.15 : (isSelected || isAdminSelected ? 1.0 : 0.8)),
                  ...(isAerial ? { icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: isSearchDimmed ? 0.15 : 1, scale: isSearchDimmed ? 1.5 : 3, strokeColor: getSegmentColor(seg) }, offset: '0', repeat: '15px' }] } : {}),
                  clickable: true,
                  zIndex: isSearchDimmed ? 1 : 2,
                }}
                onClick={() => onSelectSegment(seg)}
              />
            </React.Fragment>
          );
        })}

        {/* Handhole Markers - native Marker for smooth zoom/pan */}
        {map && showHandholes && handholesData.map((hh, idx) => {
          const isLarge = (hh.type || hh.label || '').includes('30x48');
          const isMedium = (hh.type || hh.label || '').includes('17x30') || (hh.type || hh.label || '').includes('24x36');
          const hhId = hh.device_id || hh.id || `${hh.lat}-${hh.lng}`;
          const isAdminSel = adminMode && adminSelectedDeviceIds && adminSelectedDeviceIds.has(hhId);
          const color = isLarge ? '#2196F3' : isMedium ? '#FFB800' : '#4CAF50';
          const scale = isLarge ? 11 : isMedium ? 9 : 8;

          return (
            <Marker
              key={`hh-${hh.id || idx}`}
              position={{ lat: hh.lat, lng: hh.lng }}
              title={hh.id ? `${hh.label} (${hh.id})` : hh.label}
              onClick={() => onSelectHandhole && onSelectHandhole(hh)}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: isAdminSel ? '#FFD700' : color,
                fillOpacity: 1,
                strokeColor: isAdminSel ? '#FFD700' : '#ffffff',
                strokeWeight: isAdminSel ? 3 : 2,
                scale: scale,
              }}
            />
          );
        })}

        {/* Flowerpot Markers - native Marker for smooth zoom/pan */}
        {map && showFlowerpots && flowerpots && flowerpots.map((fp, idx) => {
          const lat = parseFloat(fp.gps_lat);
          const lng = parseFloat(fp.gps_lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          const fpId = fp.device_id || `${lat}-${lng}`;
          const isFpAdminSel = adminMode && adminSelectedDeviceIds && adminSelectedDeviceIds.has(fpId);
          return (
            <Marker
              key={`fp-${idx}`}
              position={{ lat, lng }}
              title={fp.label || 'Flowerpot'}
              onClick={() => onSelectFlowerpot && onSelectFlowerpot({ ...fp, gps_lat: lat, gps_lng: lng })}
              icon={{
                path: 'M 0,-1 1,0 0,1 -1,0 Z',
                fillColor: isFpAdminSel ? '#FFD700' : '#9C27B0',
                fillOpacity: 1,
                strokeColor: isFpAdminSel ? '#FFD700' : '#ffffff',
                strokeWeight: 2,
                scale: 10,
              }}
            />
          );
        })}

        {/* Splice Point Markers - native Marker with SVG icons for smooth zoom/pan */}
        {map && showSplicePoints && splicePoints && splicePoints.map((sp, idx) => {
          const lat = parseFloat(sp.gps_lat);
          const lng = parseFloat(sp.gps_lng);
          if (isNaN(lat) || isNaN(lng)) return null;

          const spliceType = sp.splice_type || '1x4';
          const typeLabel = spliceType === '1x4' ? '1\u00d74' : (spliceType === '1x8' || spliceType === '2x8') ? '1\u00d78' : spliceType === 'F1' ? 'F1' : spliceType;
          const bgColor = spliceType === '1x4' ? '%23E91E63' : (spliceType === '1x8' || spliceType === '2x8') ? '%233F51B5' : spliceType === 'F1' ? '%23009688' : '%23607D8B';
          const isSelected = selectedSplice && (selectedSplice.splice_id === sp.splice_id || selectedSplice === sp.splice_id);
          const spId = sp.device_id || sp.splice_id || `${lat}-${lng}`;
          const isSpAdminSel = adminMode && adminSelectedDeviceIds && adminSelectedDeviceIds.has(spId);
          const borderCol = isSelected || isSpAdminSel ? '%23FFD700' : '%23ffffff';
          const pmColor = getPMStatusColor(sp);
          const pmHex = pmColor.replace('#', '%23');
          const w = 40, h = 26;
          const rawSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>` +
            `<rect x='1' y='1' width='${w-2}' height='${h-2}' rx='3' fill='${bgColor.replace(/%23/g,'#')}' stroke='${borderCol.replace(/%23/g,'#')}' stroke-width='2'/>` +
            `<text x='${w/2}' y='${h/2+1}' text-anchor='middle' dominant-baseline='central' fill='white' font-size='10' font-weight='700' font-family='Arial,sans-serif'>${typeLabel}</text>` +
            (spliceType === '1x4' ? `<circle cx='${w-2}' cy='3' r='4' fill='${pmHex.replace(/%23/g,'#')}' stroke='white' stroke-width='1'/>` : '') +
            `</svg>`;
          const svgUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(rawSvg)}`;

          return (
            <Marker
              key={`sp-${idx}`}
              position={{ lat, lng }}
              title={`${sp.splice_type || '1x4'} - ${sp.splice_id || ''}`}
              onClick={() => onSelectSplice && onSelectSplice(sp)}
              icon={{
                url: svgUrl,
                scaledSize: new window.google.maps.Size(w, h),
                anchor: new window.google.maps.Point(w / 2, h / 2),
              }}
            />
          );
        })}

        {/* Pedestal Markers — teal diamond */}
        {map && showPedestals && pedestals && pedestals.map((ped, idx) => {
          const lat = parseFloat(ped.gps_lat);
          const lng = parseFloat(ped.gps_lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          const pedId = ped.device_id || `ped-${lat}-${lng}`;
          const isPedAdminSel = adminMode && adminSelectedDeviceIds && adminSelectedDeviceIds.has(pedId);
          return (
            <Marker
              key={`ped-${idx}`}
              position={{ lat, lng }}
              title={`Pedestal ${ped.device_id || ''}`}
              onClick={() => onSelectPedestal && onSelectPedestal({ ...ped, gps_lat: lat, gps_lng: lng })}
              icon={{
                path: 'M 0,-1 1,0 0,1 -1,0 Z',
                fillColor: isPedAdminSel ? '#FFD700' : '#00897B',
                fillOpacity: 0.9,
                strokeColor: isPedAdminSel ? '#FFD700' : '#004D40',
                strokeWeight: 2,
                scale: 10,
              }}
            />
          );
        })}

        {/* Cabinet Markers — red square */}
        {map && showCabinets && cabinets && cabinets.map((cab, idx) => {
          const lat = parseFloat(cab.gps_lat);
          const lng = parseFloat(cab.gps_lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          const cabId = cab.device_id || `cab-${lat}-${lng}`;
          const isCabAdminSel = adminMode && adminSelectedDeviceIds && adminSelectedDeviceIds.has(cabId);
          return (
            <Marker
              key={`cab-${idx}`}
              position={{ lat, lng }}
              title={`Cabinet ${cab.device_id || ''}`}
              onClick={() => onSelectCabinet && onSelectCabinet({ ...cab, gps_lat: lat, gps_lng: lng })}
              icon={{
                path: 'M -1,-1 1,-1 1,1 -1,1 Z',
                fillColor: isCabAdminSel ? '#FFD700' : '#B71C1C',
                fillOpacity: 0.9,
                strokeColor: isCabAdminSel ? '#FFD700' : '#7F0000',
                strokeWeight: 2,
                scale: 11,
              }}
            />
          );
        })}
        {/* FAT markers (triangles) */}
        {map && showFats && fats && fats.map((fat, idx) => {
          const lat = parseFloat(fat.gps_lat);
          const lng = parseFloat(fat.gps_lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          return (
            <Marker
              key={`fat-${idx}`}
              position={{ lat, lng }}
              title={`FAT ${fat.device_id || ''}`}
              icon={{
                path: 'M 0,-1.2 1,0.8 -1,0.8 Z',
                fillColor: '#FF9800',
                fillOpacity: 0.85,
                strokeColor: '#E65100',
                strokeWeight: 1.5,
                scale: 7,
              }}
            />
          );
        })}
        {/* Rectangle Draw Tool for admin area-select */}
        {adminMode && drawRectMode && window.google && (
          <DrawingManager
            drawingMode={window.google.maps.drawing.OverlayType.RECTANGLE}
            onRectangleComplete={(rect) => {
              const bounds = rect.getBounds();
              if (bounds && onRectangleSelect) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                onRectangleSelect({
                  north: ne.lat(), south: sw.lat(),
                  east: ne.lng(), west: sw.lng(),
                });
              }
              rect.setMap(null);
            }}
            options={{
              drawingControl: false,
              rectangleOptions: {
                fillColor: '#FFD700',
                fillOpacity: 0.15,
                strokeColor: '#FFB800',
                strokeWeight: 2,
                clickable: false,
                editable: false,
                zIndex: 100,
              },
            }}
          />
        )}
        {/* Map Notes */}
        {showNotes && (
          <MapNotesLayer
            notes={mapNotes}
            darkMode={darkMode}
            loggedInUser={loggedInUser}
            projectId={projectId}
            section={section}
            onNotesChanged={onNotesChanged}
            isAddingNote={isAddingNote}
            onAddNoteToggle={onAddNoteToggle}
            segments={segments}
            devices={allDevices}
            isAdmin={isAdmin}
            isMobile={isMobile}
            mapZoom={mapZoom}
          />
        )}
        {/* GPS blue dot — all users */}
        {gpsOn && map && loggedInUser && (
          <MyLocationMarker
            loggedInUser={loggedInUser}
            segments={segments}
            projectId={projectId}
          />
        )}
        {/* Crew locations — admin only */}
        {map && isAdmin && (
          <CrewLocationLayer
            projectId={projectId}
            darkMode={darkMode}
            loggedInUser={loggedInUser}
          />
        )}
      </GoogleMap>

      {/* Map type controlled from Settings > Appearance */}

      {/* Enhanced Legend — collapsible */}
      <div style={{
        position: 'absolute', bottom: 0, left: 10, borderRadius: legendCollapsed ? 8 : '8px 8px 0 0',
        background: darkMode ? 'rgba(17,34,64,0.95)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(8px)', fontSize: 11, zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)', maxWidth: 180,
        overflow: 'hidden',
      }}>
        <div
          onClick={() => setLegendCollapsed(prev => !prev)}
          style={{
            fontWeight: 700, padding: '8px 14px', color: darkMode ? '#fff' : '#1e293b',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            userSelect: 'none',
          }}
        >
          <span>Legend</span>
          <span style={{ fontSize: 9, marginLeft: 8, color: darkMode ? '#8892b0' : '#64748b' }}>
            {legendCollapsed ? '▲' : '▼'}
          </span>
        </div>

        {!legendCollapsed && (
          <div style={{ padding: '0 14px 10px' }}>
            {/* Segment Status */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: darkMode ? '#8892b0' : '#64748b', marginBottom: 3 }}>
                {viewMode === 'vitruvi' ? 'Vitruvi Status' : 'Segment Status'}
              </div>
              {(viewMode === 'vitruvi' ? [
                ['Issued', VITRUVI_COLORS.issued],
                ['Assigned', VITRUVI_COLORS.assigned],
                ['Completed', VITRUVI_COLORS.completed],
                ['Verified', VITRUVI_COLORS.verified],
                ['Closed/Billed', VITRUVI_COLORS.closed],
              ] : [
                ['Not Started', C.NOT_STARTED],
                ['In Progress', C.IN_PROGRESS],
                ['Complete/QC', C.COMPLETE],
                ['Issue', C.ISSUE],
              ]).map(([label, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div style={{ width: 16, height: 3, background: color, borderRadius: 2 }} />
                  <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>{label}</span>
                </div>
              ))}
              {showAerial && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div style={{ width: 16, height: 3, borderTop: '3px dashed #4682B4' }} />
                  <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Aerial</span>
                </div>
              )}
            </div>

            {/* Markers */}
            <div style={{ borderTop: `1px solid ${darkMode ? '#1e3a5f' : '#e2e8f0'}`, paddingTop: 6 }}>
              <div style={{ fontSize: 10, color: darkMode ? '#8892b0' : '#64748b', marginBottom: 3 }}>Markers</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <div style={{ width: 10, height: 10, background: '#2196F3', borderRadius: '50%', border: '1px solid #fff' }} />
                <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Large HH</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <div style={{ width: 8, height: 8, background: '#FFB800', borderRadius: '50%', border: '1px solid #fff' }} />
                <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Medium HH</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <div style={{ width: 6, height: 6, background: '#4CAF50', borderRadius: '50%', border: '1px solid #fff' }} />
                <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Terminal Box</span>
              </div>
              {showFlowerpots && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div style={{ width: 8, height: 8, background: '#9C27B0', transform: 'rotate(45deg)', border: '1px solid #fff' }} />
                  <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Flowerpot</span>
                </div>
              )}
              {showPedestals && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div style={{ width: 8, height: 8, background: '#00897B', transform: 'rotate(45deg)', border: '1px solid #004D40' }} />
                  <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Pedestal</span>
                </div>
              )}
              {showCabinets && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div style={{ width: 8, height: 8, background: '#B71C1C', border: '1px solid #7F0000' }} />
                  <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Cabinet</span>
                </div>
              )}
              {showSplicePoints && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <div style={{ padding: '1px 3px', background: '#E91E63', borderRadius: 2, color: '#fff', fontSize: 8, fontWeight: 700 }}>1×4</div>
                    <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Splice (PM)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <div style={{ padding: '1px 3px', background: '#3F51B5', borderRadius: 2, color: '#fff', fontSize: 8, fontWeight: 700 }}>2×8</div>
                    <span style={{ color: darkMode ? '#8892b0' : '#64748b', fontSize: 10 }}>Hub Splice</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* GPS toggle button */}
      {loggedInUser && <GpsToggleButton darkMode={darkMode} mapRef={map} gpsOn={gpsOn} onToggle={onGpsToggle} />}
    </div>
  );
}
// ===== CANVAS FALLBACK MAP (no API key needed) =====
function CanvasMap({ segments, selectedSegment, onSelectSegment, filterPhase, darkMode }) {
  const canvasRef = useRef(null);
  const [hoveredSeg, setHoveredSeg] = useState(null);

  // Calculate bounds
  const bounds = useMemo(() => {
    if (segments.length === 0) return { minLat: 30.23, maxLat: 30.24, minLng: -93.39, maxLng: -93.37 };
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    segments.forEach(s => {
      const sLat = parseFloat(s.gps_start_lat) || (s.gps_start?.lat ?? 0);
      const sLng = parseFloat(s.gps_start_lng) || (s.gps_start?.lng ?? 0);
      const eLat = parseFloat(s.gps_end_lat) || (s.gps_end?.lat ?? 0);
      const eLng = parseFloat(s.gps_end_lng) || (s.gps_end?.lng ?? 0);
      minLat = Math.min(minLat, sLat, eLat);
      maxLat = Math.max(maxLat, sLat, eLat);
      minLng = Math.min(minLng, sLng, eLng);
      maxLng = Math.max(maxLng, sLng, eLng);
    });
    const padLat = (maxLat - minLat) * 0.15 || 0.001;
    const padLng = (maxLng - minLng) * 0.15 || 0.001;
    return { minLat: minLat - padLat, maxLat: maxLat + padLat, minLng: minLng - padLng, maxLng: maxLng + padLng };
  }, [segments]);

  const toCanvas = useCallback((lat, lng, width, height) => {
    const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * width;
    const y = height - ((lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * height;
    return { x, y };
  }, [bounds]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    const w = rect.width;
    const h = rect.height;

    // Background
    ctx.fillStyle = darkMode ? '#0a1628' : '#e8f0fe';
    ctx.fillRect(0, 0, w, h);

    // Grid
    ctx.strokeStyle = darkMode ? '#1e3a5f33' : '#cbd5e133';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < w; i += 40) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke(); }
    for (let i = 0; i < h; i += 40) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(w, i); ctx.stroke(); }

    // Draw segments
    const handholes = new Map();
    segments.forEach(seg => {
      const status = filterPhase === 'boring' ? seg.boring_status : filterPhase === 'pulling' ? seg.pulling_status : seg.splicing_status;
      const color = getStatusColor(status);
      const isSelected = selectedSegment && selectedSegment.segment_id === seg.segment_id;
      const isHovered = hoveredSeg === seg.segment_id;

      const start = toCanvas(parseFloat(seg.gps_start_lat), parseFloat(seg.gps_start_lng), w, h);
      const end = toCanvas(parseFloat(seg.gps_end_lat), parseFloat(seg.gps_end_lng), w, h);

      // Segment line
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = isSelected ? 5 : isHovered ? 4 : 3;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Glow effect for selected/hovered
      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.strokeStyle = color + '40';
        ctx.lineWidth = 12;
        ctx.stroke();
      }

      // Footage label
      const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
      ctx.fillStyle = darkMode ? '#ffffff' : '#1e293b';
      ctx.font = 'bold 10px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText(`${seg.footage}'`, mid.x, mid.y - 8);

      // Contractor ID label
      ctx.fillStyle = darkMode ? '#8892b0' : '#64748b';
      ctx.font = '9px system-ui';
      ctx.fillText(seg.contractor_id, mid.x, mid.y + 12);

      // Track handholes
      const startKey = `${parseFloat(seg.gps_start_lat)},${parseFloat(seg.gps_start_lng)}`;
      const endKey = `${parseFloat(seg.gps_end_lat)},${parseFloat(seg.gps_end_lng)}`;
      if (!handholes.has(startKey)) handholes.set(startKey, { pos: start, name: seg.from_handhole });
      if (!handholes.has(endKey)) handholes.set(endKey, { pos: end, name: seg.to_handhole });
    });

    // Draw handhole markers
    handholes.forEach(({ pos, name }) => {
      const isLarge = name.includes('30x48') || name.includes('24x36');
      const isMedium = name.includes('17x30');
      const radius = isLarge ? 8 : isMedium ? 6 : 5;
      const fillColor = isLarge ? '#FF9800' : isMedium ? '#2196F3' : '#4CAF50';

      ctx.beginPath();
      ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = fillColor;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Label
      ctx.fillStyle = darkMode ? '#ffffff' : '#1e293b';
      ctx.font = 'bold 10px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText(name.split(' (')[0], pos.x, pos.y - radius - 4);
    });

    // Title
    ctx.fillStyle = darkMode ? '#8892b0' : '#94a3b8';
    ctx.font = '11px system-ui';
    ctx.textAlign = 'left';
    ctx.fillText('Interactive Canvas Map (Add Google Maps API key for full features)', 10, h - 10);
  }, [segments, selectedSegment, hoveredSeg, filterPhase, darkMode, toCanvas]);

  // Click handler
  const handleCanvasClick = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Find closest segment
    let closest = null;
    let minDist = 20; // 20px threshold
    segments.forEach(seg => {
      const start = toCanvas(parseFloat(seg.gps_start_lat), parseFloat(seg.gps_start_lng), rect.width, rect.height);
      const end = toCanvas(parseFloat(seg.gps_end_lat), parseFloat(seg.gps_end_lng), rect.width, rect.height);
      // Point-to-line distance
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return;
      const t = Math.max(0, Math.min(1, ((x - start.x) * dx + (y - start.y) * dy) / (len * len)));
      const px = start.x + t * dx;
      const py = start.y + t * dy;
      const dist = Math.sqrt((x - px) * (x - px) + (y - py) * (y - py));
      if (dist < minDist) {
        minDist = dist;
        closest = seg;
      }
    });
    if (closest) onSelectSegment(closest);
  }, [segments, toCanvas, onSelectSegment]);

  // Hover handler
  const handleCanvasMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let closest = null;
    let minDist = 15;
    segments.forEach(seg => {
      const start = toCanvas(parseFloat(seg.gps_start_lat), parseFloat(seg.gps_start_lng), rect.width, rect.height);
      const end = toCanvas(parseFloat(seg.gps_end_lat), parseFloat(seg.gps_end_lng), rect.width, rect.height);
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return;
      const t = Math.max(0, Math.min(1, ((x - start.x) * dx + (y - start.y) * dy) / (len * len)));
      const px = start.x + t * dx;
      const py = start.y + t * dy;
      const dist = Math.sqrt((x - px) * (x - px) + (y - py) * (y - py));
      if (dist < minDist) { minDist = dist; closest = seg; }
    });
    setHoveredSeg(closest ? closest.segment_id : null);
    canvas.style.cursor = closest ? 'pointer' : 'default';
  }, [segments, toCanvas]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        onMouseMove={handleCanvasMove}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
      {/* Legend overlay */}
      <div style={{
        position: 'absolute', bottom: 10, left: 10, padding: '8px 12px', borderRadius: 8,
        background: darkMode ? 'rgba(17,34,64,0.92)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(8px)', fontSize: 11, zIndex: 5,
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4, color: darkMode ? '#fff' : '#1e293b' }}>Status Legend</div>
        {[
          ['Not Started', STATUS_COLORS.NOT_STARTED],
          ['In Progress', STATUS_COLORS.IN_PROGRESS],
          ['Complete', STATUS_COLORS.COMPLETE],
          ['QC Approved', STATUS_COLORS.QC_APPROVED],
          ['Issue', STATUS_COLORS.ISSUE],
        ].map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <div style={{ width: 20, height: 3, background: color, borderRadius: 2 }} />
            <span style={{ color: darkMode ? '#8892b0' : '#64748b' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// GPS coordinates collapsible — hidden by default in Info panel
function GpsCollapsible({ segment, darkMode }) {
  const [expanded, setExpanded] = useState(false);
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  return (
    <div style={{ marginTop: 8, borderTop: `1px solid ${borderColor}`, paddingTop: 6 }}>
      <button onClick={() => setExpanded(!expanded)} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        fontSize: 11, color: textMuted, display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <Navigation size={11} /> GPS Coordinates {expanded ? '▾' : '▸'}
      </button>
      {expanded && (
        <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.7, marginTop: 4 }}>
          <div><strong style={{ color: text }}>Start:</strong> {segment.gps_start_lat}, {segment.gps_start_lng}</div>
          {segment.gps_end_lat && <div><strong style={{ color: text }}>End:</strong> {segment.gps_end_lat}, {segment.gps_end_lng}</div>}
        </div>
      )}
    </div>
  );
}

// ===== SEGMENT DETAIL PANEL (v2.5.0 - Photo Upload + Auto-refresh) =====
function SegmentDetailPanel({ segment, darkMode, onClose, isAdmin, user, onSegmentUpdate, locates = [], onCreateLocate, onVerifyLocate, damageReports = [], onCreateDamageReport, showToast, colorblindMode }) {
  const [panelMode, setPanelMode] = useState(null);
  const [adminOverrides, setAdminOverrides] = useState({});
  const [adminSavingSingle, setAdminSavingSingle] = useState(false);

  // Reset to overview when segment changes
  useEffect(() => {
    setPanelMode(null);
    setAdminOverrides({});
  }, [segment?.segment_id]);

  if (!segment) return null;

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const phases = [
    { key: 'boring', label: 'Boring', status: segment.boring_status, assigned: segment.boring_assigned_to, icon: '🚧' },
    { key: 'pulling', label: 'Fiber Pulling', status: segment.pulling_status, assigned: segment.pulling_assigned_to, icon: '🚛' },
    { key: 'splicing', label: 'Splicing', status: segment.splicing_status || 'Not Started', assigned: segment.splicing_assigned_to || '', icon: '⚡' },
  ];

  // Build segment data that workflow components expect
  const segmentData = {
    ...segment,
    segment_id: segment.segment_id,
    project_id: segment.segment_id?.split('-').slice(0, 3).join('-') || null,
    boring_photos: segment.boring_photos || [],
    pulling_photos: segment.pulling_photos || [],
    splicing_photos: segment.splicing_photos || [],
    boring_actual_footage: segment.boring_actual_footage || segment.footage,
    pulling_cable_type: segment.pulling_cable_type || '',
    pulling_direction: segment.pulling_direction || '',
    handhole_type: segment.to_handhole?.match(/\(([^)]+)\)/)?.[1] || '15x20x12',
  };

  // Placeholder handlers for workflow state changes
  const handleStatusChange = async (phase, statusData) => {
    // statusData comes from workflow trackers - may be string or object
    const newStatus = typeof statusData === 'string' ? statusData : (statusData?.value || statusData?.newStatus || statusData);
    const segId = segment.segment_id;
    const field = phase === 'boring' ? 'boring_status' : phase === 'pulling' ? 'pulling_status' : 'splicing_status';
    console.log(`[ProjectMap] DB write: ${segId}.${field} = ${newStatus}`);

    // Write to Google Sheets
    const ok = await updateSegmentField(segId, field, newStatus);
    if (ok) {
      // Log the action
      await logAction(segment.project_id || null, segId, user?.email, `${phase}_status_changed`, { from: segment[field], to: newStatus, notes: statusData?.notes });
      
      // Write timestamp if completing
      if (newStatus === 'Complete' || newStatus === 'QC Approved') {
        const tsField = phase === 'boring' ? 'boring_completed' : phase === 'pulling' ? 'pulling_completed' : 'completed';
        await updateSegmentField(segId, tsField, new Date().toISOString());
      }
      if (newStatus === 'In Progress') {
        const tsField = phase === 'boring' ? 'boring_started' : phase === 'pulling' ? 'pulling_started' : 'started';
        await updateSegmentField(segId, tsField, new Date().toISOString());
      }
      if (newStatus === 'QC Approved') {
        const qcField = phase === 'boring' ? 'boring_qc_approved_by' : phase === 'pulling' ? 'pulling_qc_approved_by' : 'qc_approved_by';
        const qcDateField = phase === 'boring' ? 'boring_qc_approved_date' : phase === 'pulling' ? 'pulling_qc_approved_date' : 'qc_approved_date';
        await updateSegmentField(segId, qcField, user?.name || user?.email || 'Admin');
        await updateSegmentField(segId, qcDateField, new Date().toISOString());
      }
      if (statusData?.actual_footage) {
        await updateSegmentField(segId, 'boring_actual_footage', statusData.actual_footage);
      }
      if (statusData?.notes) {
        const notesField = phase === 'boring' ? 'boring_notes' : phase === 'pulling' ? 'pulling_notes' : 'notes';
        await updateSegmentField(segId, notesField, statusData.notes);
      }

      // Notify admins when segment flagged as Issue (C4: WORKFLOW Step 84)
      if (newStatus === 'Issue') {
        try {
          const { sendEmailNotification, addNotification } = await import('../services/notificationService');
          const street = segment?.street || 'Unknown';
          const admins = ['matt@lytcomm.com', 'mason@lytcomm.com', 'donnie@lytcomm.com'];
          for (const admin of admins) {
            await sendEmailNotification(admin, `Field Issue: ${segId} — ${street}`,
              `<p>Segment <strong>${segId}</strong> flagged as <strong style="color:#FF9800">Issue</strong> during <strong>${phase}</strong>.</p>
               <p><strong>Street:</strong> ${street}</p>
               ${statusData?.notes ? `<p><strong>Notes:</strong> ${statusData.notes}</p>` : ''}
               <p><a href="https://lytcomm.com/#project-map">View on Map</a></p>`);
          }
          addNotification({ type: 'field_issue', severity: 'urgent',
            title: 'Field Issue', message: `${segId} — ${street}` });
        } catch (err) {
          console.warn('[ProjectMap] Issue notification failed:', err);
        }
      }

      // Notify admins when segment marked Complete — QC needed (WORKFLOW Step 89)
      if (newStatus === 'Complete') {
        try {
          const { notifyQCNeeded } = await import('../services/notificationService');
          const assignedField = phase === 'boring' ? 'boring_assigned_to'
            : phase === 'pulling' ? 'pulling_assigned_to' : 'splicing_assigned_to';
          const submittedBy = segment[assignedField] || user?.name || 'Unknown';
          await notifyQCNeeded(segId, phase, submittedBy);
        } catch (err) { console.warn('[ProjectMap] QC needed notification failed:', err); }
      }

      // Notify contractor when QC Approved (WORKFLOW Step 96)
      if (newStatus === 'QC Approved') {
        try {
          const { notifyQCResult } = await import('../services/notificationService');
          const { lookupContractorEmail } = await import('../services/mapService');
          const assignedField = phase === 'boring' ? 'boring_assigned_to'
            : phase === 'pulling' ? 'pulling_assigned_to' : 'splicing_assigned_to';
          const companyName = segment[assignedField];
          if (companyName) {
            const email = await lookupContractorEmail(companyName);
            if (email) await notifyQCResult(email, segId, phase, true, statusData?.notes || null);
          }
        } catch (err) { console.warn('[ProjectMap] QC approved notification failed:', err); }
      }

      // Notify contractor when QC rejected — reverted to In Progress (WORKFLOW Step 98)
      if (newStatus === 'In Progress' && (segment[field] === 'Complete' || segment[field] === 'QC Review')) {
        try {
          const { notifyQCResult } = await import('../services/notificationService');
          const { lookupContractorEmail } = await import('../services/mapService');
          const assignedField = phase === 'boring' ? 'boring_assigned_to'
            : phase === 'pulling' ? 'pulling_assigned_to' : 'splicing_assigned_to';
          const companyName = segment[assignedField];
          if (companyName) {
            const email = await lookupContractorEmail(companyName);
            if (email) await notifyQCResult(email, segId, phase, false,
              statusData?.notes || 'Work returned to In Progress — please review and address issues.');
          }
        } catch (err) { console.warn('[ProjectMap] QC rejected notification failed:', err); }
      }

      // Notify parent to refresh local state
      if (onSegmentUpdate) onSegmentUpdate(segId, field, newStatus);
      console.log(`[ProjectMap] ✅ DB updated: ${segId}.${field} = ${newStatus}`);
    } else {
      console.error(`[ProjectMap] ❌ DB write failed: ${segId}.${field}`);
    }
  };

  const handlePhotoUpload = async (phase, photos) => {
    console.log(`[ProjectMap] Photo upload: ${segment.segment_id} ${phase}`, photos);
    if (!photos || photos.length === 0) return;
    
    const projectId = segment.project_id || null;
    const segId = segment.segment_id;
    
    try {
      // Upload batch to Google Drive
      const results = await uploadPhotoBatch(
        photos.map((p, i) => ({ file: p.file || p, type: `${phase}_photo_${i + 1}` })),
        projectId,
        segId,
        (current, total) => console.log(`[PhotoUpload] ${current}/${total}`)
      );
      
      const successCount = results.filter(r => r.success).length;
      const cachedCount = results.filter(r => r.cached).length;
      
      // Store photo URLs in segment
      const photoUrls = results.filter(r => r.url).map(r => r.url);
      if (photoUrls.length > 0) {
        const photoField = `${phase}_photos`;
        let existing = [];
        try { if (segment[photoField]) existing = JSON.parse(segment[photoField]); } catch (e) {}
        if (!Array.isArray(existing)) existing = [];
        await updateSegmentField(segId, photoField, JSON.stringify([...existing, ...photoUrls]));
      }
      
      // Log the upload
      await logAction(projectId, segId, user?.email, `${phase}_photos_uploaded`, {
        count: photos.length, success: successCount, cached: cachedCount,
      });
      
      console.log(`[ProjectMap] ✅ Photos uploaded: ${successCount} success, ${cachedCount} cached`);
    } catch (err) {
      console.error('[ProjectMap] ❌ Photo upload failed:', err);
    }
  };

  // Per-phase admin save
  const handleAdminSave = async (phase) => {
    setAdminSavingSingle(true);
    const segId = segment.segment_id;
    const now = new Date().toISOString();
    const who = user?.name || user?.email || 'admin';
    const phaseFields = [`${phase}_status`, `${phase}_billing_status`];
    try {
      for (const field of phaseFields) {
        const value = adminOverrides[field];
        if (value === undefined || value === segment[field]) continue;
        await updateSegmentField(segId, field, value);
        if (onSegmentUpdate) onSegmentUpdate(segId, field, value);
        if (field.endsWith('_status') && value === 'QC Approved') {
          const p = field.replace('_status', '');
          const qcBy = p === 'boring' ? 'boring_qc_approved_by' : p === 'pulling' ? 'pulling_qc_approved_by' : 'qc_approved_by';
          const qcDate = p === 'boring' ? 'boring_qc_approved_date' : p === 'pulling' ? 'pulling_qc_approved_date' : 'qc_approved_date';
          await updateSegmentField(segId, qcBy, who);
          await updateSegmentField(segId, qcDate, now);
        }
        if (field.endsWith('_status') && (value === 'Complete' || value === 'QC Approved')) {
          const p = field.replace('_status', '');
          const tsField = p === 'boring' ? 'boring_completed' : p === 'pulling' ? 'pulling_completed' : 'completed';
          await updateSegmentField(segId, tsField, now);
        }
      }
      await logAction(segment.project_id || null, segId, user?.email, 'admin_override',
        Object.fromEntries(phaseFields.filter(f => adminOverrides[f] !== undefined).map(f => [f, adminOverrides[f]])));
      setAdminOverrides(prev => {
        const next = { ...prev };
        phaseFields.forEach(f => delete next[f]);
        return next;
      });
      if (showToast) showToast(`${phase} overrides saved`, 'success');
    } catch (err) {
      console.error('[AdminSave] Error:', err);
      if (showToast) showToast('Failed to save overrides', 'error');
    }
    setAdminSavingSingle(false);
  };

  // Admin override block for a single phase
  const AdminOverrideBlock = ({ phase }) => {
    if (!isAdmin) return null;
    const statusField = `${phase}_status`;
    const billingField = `${phase}_billing_status`;
    const currentStatus = adminOverrides[statusField] ?? segment[statusField] ?? 'Not Started';
    const currentBilling = adminOverrides[billingField] ?? segment[billingField] ?? 'Not Billed';
    const statusOptions = phase === 'boring'
      ? ['Not Started', 'Potholing', 'Pothole Approved', 'In Progress', 'Complete', 'QC Approved']
      : ['Not Started', 'In Progress', 'Complete', 'QC Approved'];
    const hasChanges = (adminOverrides[statusField] !== undefined && adminOverrides[statusField] !== segment[statusField])
      || (adminOverrides[billingField] !== undefined && adminOverrides[billingField] !== segment[billingField]);
    const selectStyle = { flex: 1, padding: '8px 10px', borderRadius: 6, border: `2px solid ${darkMode ? '#2a4a7f' : '#94a3b8'}`, background: darkMode ? '#0d1b2a' : '#f1f5f9', color: text, fontSize: 13, fontWeight: 500, cursor: 'pointer' };

    return (
      <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginTop: 12, border: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Shield size={16} color={accent} />
          <span style={{ fontWeight: 700, color: text, fontSize: 13 }}>Admin Override</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: text, width: 55, fontWeight: 600 }}>Status</span>
          <select value={currentStatus} onChange={e => setAdminOverrides(prev => ({ ...prev, [statusField]: e.target.value }))} style={selectStyle}>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: text, width: 55, fontWeight: 600 }}>Billing</span>
          <select value={currentBilling} onChange={e => setAdminOverrides(prev => ({ ...prev, [billingField]: e.target.value }))} style={selectStyle}>
            {['Not Billed', 'Submitted', 'Approved', 'Paid'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {hasChanges && (
          <button disabled={adminSavingSingle} onClick={() => handleAdminSave(phase)}
            style={{ width: '100%', padding: '8px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#4CAF50', color: '#fff', cursor: adminSavingSingle ? 'not-allowed' : 'pointer', opacity: adminSavingSingle ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Save size={12} /> {adminSavingSingle ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>
    );
  };

  // Pipeline status strip stages
  const stripStages = [
    { key: 'locates', label: 'LOC', color: (() => { const sl = locates.filter(l => l.segment_id === segment.segment_id); if (!sl.length) return '#9E9E9E'; if (sl.some(l => l.status === 'Expired')) return '#FF4444'; if (sl.every(l => l.status === 'Verified')) return '#4CAF50'; if (sl.some(l => l.status === 'Received')) return '#2196F3'; return '#FFB800'; })() },
    { key: 'boring', label: 'BOR', color: getStatusColor(segment.boring_status || 'Not Started', colorblindMode) },
    { key: 'pulling', label: 'PUL', color: getStatusColor(segment.pulling_status || 'Not Started', colorblindMode) },
    { key: 'splicing', label: 'SPL', color: getStatusColor(segment.splicing_status || 'Not Started', colorblindMode) },
    { key: 'restoration', label: 'RST', color: getStatusColor(segment.restoration_status || 'Not Started', colorblindMode) },
  ];

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      background: darkMode ? '#0d1b2a' : '#ffffff',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: text }}>{segment.contractor_id}</div>
          <div style={{ fontSize: 11, color: textMuted }}>{segment.segment_id}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
          <X size={20} />
        </button>
      </div>

      {/* Pipeline Status Strip */}
      <div style={{
        display: 'flex', gap: 2, padding: '8px 8px',
        borderBottom: `1px solid ${borderColor}`, flexShrink: 0,
        background: darkMode ? '#0a1628' : '#f8fafc',
        boxSizing: 'border-box', width: '100%', overflow: 'hidden',
      }}>
        {stripStages.map(s => (
          <button key={s.key} onClick={() => setPanelMode(s.key)} style={{
            flex: '1 1 0', minWidth: 0, padding: '6px 0', background: s.color, color: '#fff',
            border: panelMode === s.key ? '2px solid #fff' : '2px solid transparent',
            borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', textAlign: 'center',
            boxShadow: panelMode === s.key ? `0 0 0 1px ${s.color}` : 'none',
            transition: 'all 0.15s', boxSizing: 'border-box',
          }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>

        {/* Back button (all modes except overview) */}
        {panelMode !== null && (
          <button onClick={() => setPanelMode(null)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '10px 16px', background: 'none', border: 'none',
            cursor: 'pointer', color: accent, fontSize: 12, fontWeight: 600,
          }}>
            <ArrowLeft size={14} /> Pipeline Overview
          </button>
        )}

        {/* ===== PIPELINE OVERVIEW (landing) ===== */}
        {panelMode === null && (
          <div style={{ padding: '14px 16px' }}>
            {/* Compact identity — dense summary */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
              {/* Row 1: Segment ID + footage */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: text }}>{segment.segment_id}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: accent }}>{segment.footage} <span style={{ fontSize: 11, fontWeight: 400 }}>LF</span></span>
              </div>
              {/* Row 2: Street or HH path */}
              <div style={{ fontSize: 12, color: textMuted, marginBottom: 2 }}>
                {segment.street && segment.street !== 'Unknown Street'
                  ? segment.street
                  : `${segment.from_handhole || '?'} → ${segment.to_handhole || '?'}`}
              </div>
              {/* Row 3: Section */}
              <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>
                Section: {segment.section || '—'}
                {segment.street && segment.street !== 'Unknown Street' && (segment.from_handhole || segment.to_handhole) && (
                  <span style={{ marginLeft: 8 }}>{segment.from_handhole} → {segment.to_handhole}</span>
                )}
              </div>
              {/* Row 4: Bore code + contractor */}
              <div style={{ fontSize: 11, color: textMuted, marginBottom: 8, display: 'flex', gap: 12 }}>
                {segment.bore_code && (
                  <span>Type: <strong style={{ color: text }}>{segment.bore_code}</strong></span>
                )}
                {segment.boring_assigned_to && (
                  <span>Contractor: <strong style={{ color: text }}>{segment.boring_assigned_to}</strong></span>
                )}
              </div>
              {/* Row 5: Phase statuses on one line */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                {[
                  { label: 'BOR', status: segment.boring_status || 'Not Started' },
                  { label: 'PUL', status: segment.pulling_status || 'Not Started' },
                  { label: 'SPL', status: segment.splicing_status || 'Not Started' },
                ].map(p => (
                  <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                    <span style={{ fontWeight: 700, color: textMuted }}>{p.label}</span>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: getStatusColor(p.status, colorblindMode), display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: getStatusColor(p.status, colorblindMode), fontWeight: 600, fontSize: 10 }}>{p.status === 'Not Started' ? '—' : p.status}</span>
                  </div>
                ))}
              </div>
              {/* Row 6: Billing statuses (only if any are non-default) */}
              {(segment.boring_billing_status && segment.boring_billing_status !== 'Not Billed') || (segment.pulling_billing_status && segment.pulling_billing_status !== 'Not Billed') ? (
                <div style={{ fontSize: 10, color: textMuted, borderTop: `1px solid ${borderColor}`, paddingTop: 6 }}>
                  Billing: {['boring', 'pulling'].map(p => {
                    const bs = segment[`${p}_billing_status`];
                    if (!bs || bs === 'Not Billed') return null;
                    return <span key={p} style={{ marginRight: 10 }}><strong>{p === 'boring' ? 'BOR' : 'PUL'}</strong> {bs}</span>;
                  })}
                </div>
              ) : null}
              <button onClick={() => setPanelMode('info')} style={{
                marginTop: 6, background: 'none', border: 'none', color: accent, fontSize: 11, cursor: 'pointer', padding: 0,
              }}>
                All details →
              </button>
            </div>

            {/* Mode chooser */}
            <div style={{ fontWeight: 600, color: text, marginBottom: 8, fontSize: 13 }}>What are you working on?</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              {[
                { key: 'info', label: 'Info', icon: <MapPin size={16} color={accent} />, sub: 'Location, GPS' },
                { key: 'locates', label: 'Locates', icon: <Radio size={16} color="#f59e0b" />, sub: `${locates.filter(l => l.segment_id === segment.segment_id).length} tickets` },
                ...phases.map(p => ({
                  key: p.key,
                  label: p.label,
                  icon: <span style={{ fontSize: 16 }}>{p.icon}</span>,
                  status: p.status,
                  billing: segment[`${p.key}_billing_status`],
                  assigned: p.assigned,
                })),
                { key: 'restoration', label: 'Restoration', icon: <HardHat size={16} color="#f59e0b" />, sub: segment.restoration_status || 'Not Started' },
              ].map(item => (
                <div key={item.key} onClick={() => setPanelMode(item.key)} style={{
                  background: cardBg, borderRadius: 10, padding: 12,
                  border: `1px solid ${borderColor}`,
                  borderLeft: item.status ? `4px solid ${getStatusColor(item.status, colorblindMode)}` : `4px solid ${borderColor}`,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#162b4d' : '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = cardBg}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {item.icon}
                    <span style={{ fontWeight: 600, color: text, fontSize: 12 }}>{item.label}</span>
                  </div>
                  {item.status && (
                    <div style={{
                      display: 'inline-block', padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 600,
                      background: getStatusColor(item.status, colorblindMode) + '20', color: getStatusColor(item.status, colorblindMode),
                      marginBottom: 2,
                    }}>{item.status}</div>
                  )}
                  {item.billing && item.billing !== 'Not Billed' && (
                    <div style={{
                      display: 'inline-block', marginLeft: 4, padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 600,
                      background: ((colorblindMode ? BILLING_STATUS_COLORS_CB : BILLING_STATUS_COLORS)[item.billing] || '#9E9E9E') + '20',
                      color: (colorblindMode ? BILLING_STATUS_COLORS_CB : BILLING_STATUS_COLORS)[item.billing] || '#9E9E9E',
                    }}>{item.billing}</div>
                  )}
                  {item.assigned && (
                    <div style={{ fontSize: 10, color: textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Users size={9} /> {item.assigned}
                    </div>
                  )}
                  {item.sub && !item.status && (
                    <div style={{ fontSize: 10, color: textMuted }}>{item.sub}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Billing summary */}
            {(segment.boring_billing_status || segment.pulling_billing_status) && (
              <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <DollarSign size={14} color={accent} />
                  <span style={{ fontWeight: 600, color: text, fontSize: 12 }}>Billing</span>
                </div>
                {[
                  { label: 'Boring', status: segment.boring_billing_status || 'Not Billed' },
                  { label: 'Pulling', status: segment.pulling_billing_status || 'Not Billed' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                    <span style={{ fontSize: 11, color: textMuted }}>{item.label}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                      background: ((colorblindMode ? BILLING_STATUS_COLORS_CB : BILLING_STATUS_COLORS)[item.status] || '#9E9E9E') + '20',
                      color: (colorblindMode ? BILLING_STATUS_COLORS_CB : BILLING_STATUS_COLORS)[item.status] || '#9E9E9E',
                    }}>{item.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== INFO MODE ===== */}
        {panelMode === 'info' && (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 10, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <MapPin size={15} color={accent} />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Location</span>
              </div>
              <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.7 }}>
                <div><strong style={{ color: text }}>From:</strong> {segment.from_handhole}</div>
                <div><strong style={{ color: text }}>To:</strong> {segment.to_handhole}</div>
                <div><strong style={{ color: text }}>Street:</strong> {segment.street}</div>
                <div><strong style={{ color: text }}>Section:</strong> {segment.section}</div>
              </div>
            </div>
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 10, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Ruler size={15} color={accent} />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Footage</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: accent }}>{segment.footage} <span style={{ fontSize: 13, fontWeight: 400 }}>LF</span></div>
            </div>
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
              <div style={{ fontWeight: 600, color: text, fontSize: 13, marginBottom: 8 }}>Identity</div>
              <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.7 }}>
                <div><strong style={{ color: text }}>Segment ID:</strong> {segment.segment_id}</div>
                <div><strong style={{ color: text }}>Contractor:</strong> {segment.contractor_id || '—'}</div>
                <div><strong style={{ color: text }}>Project:</strong> {segment.project_id || segmentData.project_id || '—'}</div>
                {segment.bore_code && <div><strong style={{ color: text }}>Bore Code:</strong> {segment.bore_code}</div>}
                {segment.pull_code && <div><strong style={{ color: text }}>Pull Code:</strong> {segment.pull_code}</div>}
                {segment.cable_type && <div><strong style={{ color: text }}>Cable:</strong> {segment.cable_type}</div>}
                {segment.duct_count && <div><strong style={{ color: text }}>Ducts:</strong> {segment.duct_count}</div>}
              </div>
              {/* GPS — collapsible */}
              {segment.gps_start_lat && (
                <GpsCollapsible segment={segment} darkMode={darkMode} />
              )}
            </div>
          </div>
        )}

        {/* ===== BORING MODE ===== */}
        {panelMode === 'boring' && (
          <div style={{ padding: '8px' }}>
            <BoringTracker
              segment={segmentData}
              darkMode={darkMode}
              user={user}
              locates={locates}
              onStatusUpdate={(status) => handleStatusChange('boring', status)}
              onPhotoUpload={(photos) => handlePhotoUpload('boring', photos)}
            />
            {(segmentData?.boring_status === 'In Progress' || segmentData?.boring_status === 'Potholing') && onCreateDamageReport && (
              <DamageReport
                segment={segmentData}
                darkMode={darkMode}
                user={user}
                existingReports={damageReports}
                onSubmit={onCreateDamageReport}
              />
            )}
            <AdminOverrideBlock phase="boring" />
          </div>
        )}

        {/* ===== PULLING MODE ===== */}
        {panelMode === 'pulling' && (
          <div style={{ padding: '8px' }}>
            <PullingTracker
              segment={segmentData}
              darkMode={darkMode}
              user={user}
              isAdmin={isAdmin || false}
              isSplicer={user?.role === 'Splicer' || false}
              onStatusUpdate={(status) => handleStatusChange('pulling', status)}
              onPhotoUpload={(photos) => handlePhotoUpload('pulling', photos)}
              onQCApprove={(data) => handleStatusChange('pulling', 'QC Approved')}
            />
            <AdminOverrideBlock phase="pulling" />
          </div>
        )}

        {/* ===== SPLICING MODE ===== */}
        {panelMode === 'splicing' && (
          <div style={{ padding: '8px' }}>
            <SplicingTracker
              segment={segmentData}
              darkMode={darkMode}
              user={user}
              isAdmin={isAdmin || false}
              onStatusUpdate={(status) => handleStatusChange('splicing', status)}
              onPhotoUpload={(photos) => handlePhotoUpload('splicing', photos)}
              onQCApprove={(data) => handleStatusChange('splicing', 'QC Approved')}
            />
            <AdminOverrideBlock phase="splicing" />
          </div>
        )}

        {/* ===== LOCATES MODE ===== */}
        {panelMode === 'locates' && (
          <div style={{ padding: '8px' }}>
            <LocateTracker
              segment={segmentData}
              locates={locates}
              darkMode={darkMode}
              user={user}
              onCreateLocate={onCreateLocate}
              onVerifyLocate={onVerifyLocate}
            />
          </div>
        )}

        {/* ===== RESTORATION MODE ===== */}
        {panelMode === 'restoration' && (
          <div style={{ padding: '8px' }}>
            <RestorationTracker
              segment={segmentData}
              darkMode={darkMode}
              user={user}
              onStatusUpdate={(phase, status, data) => {
                handleStatusChange(phase, status);
              }}
            />
          </div>
        )}
      </div>

      {/* Version tag */}
      <div style={{ padding: '4px 16px', fontSize: 9, color: 'transparent', textAlign: 'right', userSelect: 'none', flexShrink: 0 }}>v3.0.0</div>
    </div>
  );
}

// ===== SPLICE POINT DETAIL PANEL =====
function SplicePointDetailPanel({ splicePoint, darkMode, onClose, user, onSpliceUpdate }) {
  const [pmReadings, setPmReadings] = useState([]);
  const [editingReading, setEditingReading] = useState(null);
  const [inputValue, setInputValue] = useState('');

  // Parse PM readings on load
  useEffect(() => {
    if (!splicePoint) return;
    if (splicePoint.splice_type === '1x4') {
      // Initialize or parse existing PM readings
      let readings = [];
      if (splicePoint.pm_readings) {
        try {
          readings = typeof splicePoint.pm_readings === 'string'
            ? JSON.parse(splicePoint.pm_readings)
            : splicePoint.pm_readings;
        } catch (e) { readings = []; }
      }
      // Ensure we have all 8 readings for 1x4
      const ports = ['SA1P1', 'SA1P2', 'SA1P3', 'SA1P4', 'SB1P5', 'SB1P6', 'SB1P7', 'SB1P8'];
      const fullReadings = ports.map(port => {
        const existing = readings.find(r => r.port === port);
        return existing || { port, value_dBm: null, status: 'pending', timestamp: null };
      });
      setPmReadings(fullReadings);
    }
  }, [splicePoint]);

  if (!splicePoint) return null;

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const spliceType = splicePoint.splice_type || '1x4';
  const is1x4 = spliceType === '1x4';
  const photoReqs = PHOTO_REQUIREMENTS[spliceType] || PHOTO_REQUIREMENTS['1x4'];

  // PM reading status color
  const getPMColor = (status) => {
    switch (status) {
      case 'pass': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'fail': return '#F44336';
      case 'no_light': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  // Handle PM value input
  const handlePMInput = async (port, value) => {
    const numValue = parseFloat(value);
    let status = 'pending';

    if (value === 'no_light' || value === 'NL') {
      status = 'no_light';
    } else if (!isNaN(numValue)) {
      status = evaluatePMReading(numValue);
    }

    const updatedReadings = pmReadings.map(r =>
      r.port === port
        ? { ...r, value_dBm: isNaN(numValue) ? null : numValue, status, timestamp: new Date().toISOString() }
        : r
    );
    setPmReadings(updatedReadings);
    setEditingReading(null);
    setInputValue('');

    // Save to database
    if (onSpliceUpdate) {
      await onSpliceUpdate(splicePoint.splice_id, 'pm_readings', JSON.stringify(updatedReadings));
    }
  };

  // Count PM stats
  const pmStats = pmReadings.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      background: darkMode ? '#0d1b2a' : '#ffffff',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700,
              background: spliceType === '1x4' ? '#E91E63' : (spliceType === '1x8' || spliceType === '2x8') ? '#3F51B5' : '#009688',
              color: '#fff',
            }}>
              {spliceType}
            </div>
            <span style={{ fontSize: 17, fontWeight: 700, color: text }}>
              {splicePoint.contractor_id || splicePoint.location || 'Splice Point'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: textMuted }}>{splicePoint.splice_id}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>
        {/* Location Info */}
        <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <MapPin size={15} color={accent} />
            <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Location</span>
          </div>
          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.7 }}>
            <div><strong style={{ color: text }}>Handhole:</strong> {splicePoint.location || splicePoint.handhole_type || '—'}</div>
            <div><strong style={{ color: text }}>Position:</strong> {splicePoint.position_type || '—'}</div>
            <div><strong style={{ color: text }}>Status:</strong> {splicePoint.status || 'Not Started'}</div>
          </div>
        </div>

        {/* Photo Requirements */}
        <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Camera size={15} color={accent} />
            <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Photo Requirements</span>
          </div>
          <div style={{ fontSize: 12, color: textMuted }}>
            <div style={{ marginBottom: 4 }}>
              <strong style={{ color: text }}>Enclosure:</strong> {photoReqs?.enclosureCount || 7} photos
            </div>
            {is1x4 && (
              <div style={{ marginBottom: 4 }}>
                <strong style={{ color: text }}>PM Photos:</strong> {photoReqs?.pmPhotoCount || 2} (1 per splitter)
              </div>
            )}
            <div style={{ fontWeight: 600, color: accent }}>
              Total: {photoReqs?.totalPhotos || (is1x4 ? 9 : 8)} photos required
            </div>
          </div>
        </div>

        {/* PM Readings (1x4 only) */}
        {is1x4 && (
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Activity size={15} color={accent} />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Power Meter Readings</span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {pmStats.pass > 0 && <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: '#4CAF5020', color: '#4CAF50' }}>{pmStats.pass} Pass</span>}
                {pmStats.warning > 0 && <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: '#FF980020', color: '#FF9800' }}>{pmStats.warning} Warn</span>}
                {pmStats.fail > 0 && <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: '#F4433620', color: '#F44336' }}>{pmStats.fail} Fail</span>}
              </div>
            </div>

            {/* Thresholds Reference */}
            <div style={{ fontSize: 10, color: textMuted, marginBottom: 10, padding: '6px 8px', background: darkMode ? '#0a1628' : '#f1f5f9', borderRadius: 6 }}>
              <strong>Thresholds:</strong> Pass: -8 to -25 dBm | Warning: -25 to -28 dBm | Fail: &lt;-28 or &gt;-8 dBm
            </div>

            {/* Splitter A */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 6 }}>Splitter A</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {pmReadings.slice(0, 4).map(reading => (
                  <div key={reading.port} style={{
                    background: darkMode ? '#0a1628' : '#fff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 6, padding: 8, textAlign: 'center',
                    borderLeft: `3px solid ${getPMColor(reading.status)}`,
                  }}>
                    <div style={{ fontSize: 10, color: textMuted, marginBottom: 2 }}>{reading.port}</div>
                    {editingReading === reading.port ? (
                      <input
                        type="text"
                        autoFocus
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handlePMInput(reading.port, inputValue);
                          if (e.key === 'Escape') { setEditingReading(null); setInputValue(''); }
                        }}
                        onBlur={() => { setEditingReading(null); setInputValue(''); }}
                        placeholder="-20.5"
                        style={{
                          width: '100%', padding: '2px 4px', fontSize: 12, textAlign: 'center',
                          border: `1px solid ${accent}`, borderRadius: 4, outline: 'none',
                          background: darkMode ? '#112240' : '#fff', color: text,
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => { setEditingReading(reading.port); setInputValue(reading.value_dBm?.toString() || ''); }}
                        style={{
                          fontSize: 14, fontWeight: 700, color: getPMColor(reading.status),
                          cursor: 'pointer', minHeight: 20,
                        }}
                      >
                        {reading.status === 'no_light' ? 'NL' : reading.value_dBm !== null ? `${reading.value_dBm}` : '—'}
                      </div>
                    )}
                    <div style={{ fontSize: 9, color: getPMColor(reading.status), marginTop: 2 }}>
                      {reading.status === 'pending' ? 'Tap to enter' : reading.status.toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Splitter B */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: text, marginBottom: 6 }}>Splitter B</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {pmReadings.slice(4, 8).map(reading => (
                  <div key={reading.port} style={{
                    background: darkMode ? '#0a1628' : '#fff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 6, padding: 8, textAlign: 'center',
                    borderLeft: `3px solid ${getPMColor(reading.status)}`,
                  }}>
                    <div style={{ fontSize: 10, color: textMuted, marginBottom: 2 }}>{reading.port}</div>
                    {editingReading === reading.port ? (
                      <input
                        type="text"
                        autoFocus
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handlePMInput(reading.port, inputValue);
                          if (e.key === 'Escape') { setEditingReading(null); setInputValue(''); }
                        }}
                        onBlur={() => { setEditingReading(null); setInputValue(''); }}
                        placeholder="-20.5"
                        style={{
                          width: '100%', padding: '2px 4px', fontSize: 12, textAlign: 'center',
                          border: `1px solid ${accent}`, borderRadius: 4, outline: 'none',
                          background: darkMode ? '#112240' : '#fff', color: text,
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => { setEditingReading(reading.port); setInputValue(reading.value_dBm?.toString() || ''); }}
                        style={{
                          fontSize: 14, fontWeight: 700, color: getPMColor(reading.status),
                          cursor: 'pointer', minHeight: 20,
                        }}
                      >
                        {reading.status === 'no_light' ? 'NL' : reading.value_dBm !== null ? `${reading.value_dBm}` : '—'}
                      </div>
                    )}
                    <div style={{ fontSize: 9, color: getPMColor(reading.status), marginTop: 2 }}>
                      {reading.status === 'pending' ? 'Tap to enter' : reading.status.toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* No Light Button */}
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button
                onClick={() => {
                  const pendingPort = pmReadings.find(r => r.status === 'pending')?.port;
                  if (pendingPort) handlePMInput(pendingPort, 'no_light');
                }}
                style={{
                  padding: '6px 16px', fontSize: 11, fontWeight: 600,
                  background: '#2196F320', color: '#2196F3', border: '1px solid #2196F3',
                  borderRadius: 6, cursor: 'pointer',
                }}
              >
                Mark Next as "No Light" (Fiber not lit)
              </button>
            </div>
          </div>
        )}

        {/* Non-1x4 info */}
        {!is1x4 && (
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Radio size={15} color={accent} />
              <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Splice Info</span>
            </div>
            <div style={{ fontSize: 12, color: textMuted }}>
              {(spliceType === '1x8' || spliceType === '2x8') && (
                <div style={{ padding: '8px 12px', background: '#3F51B520', borderRadius: 6, color: '#3F51B5' }}>
                  <strong>Hub Location (1x8)</strong><br />
                  No PM testing required. 8 enclosure photos needed.
                </div>
              )}
              {spliceType === 'F1' && (
                <div style={{ padding: '8px 12px', background: '#00968820', borderRadius: 6, color: '#009688' }}>
                  <strong>F1 Splice ({splicePoint.fiber_count || 432} fiber)</strong><br />
                  {splicePoint.tray_count || 8} trays. Photos: 5 base + 1 per tray.
                </div>
              )}
              {spliceType === 'TYCO-D' && (
                <div style={{ padding: '8px 12px', background: '#60748b20', borderRadius: 6, color: '#607D8B' }}>
                  <strong>TYCO-D Splice</strong><br />
                  9 splices. Photos: 5 base + 1 per tray.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Version tag */}
      <div style={{ padding: '4px 16px', fontSize: 9, color: 'transparent', textAlign: 'right', userSelect: 'none', flexShrink: 0 }}>SplicePanel v1.0.0</div>
    </div>
  );
}

// ===== HANDHOLE DETAIL PANEL =====
function HandholeDetailPanel({ handhole, segments, darkMode, onClose }) {
  if (!handhole) return null;

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const label = handhole.label || handhole.id || 'Handhole';
  const hhType = handhole.type || '';
  const code = handhole.vendor_codes?.[0]?.code || handhole.code || 'UG20';
  const isLarge = hhType.includes('30x48') || label.includes('30x48');
  const isMedium = hhType.includes('17x30') || hhType.includes('24x36') || label.includes('17x30') || label.includes('24x36');
  const sizeLabel = isLarge ? '30×48×24 (Large)' : isMedium ? '17×30×18 (Medium)' : '15×20×12 (Small)';
  const sizeColor = isLarge ? '#2196F3' : isMedium ? '#FFB800' : '#4CAF50';

  // Find connected segments
  const connected = (segments || []).filter(seg => {
    const sLat = parseFloat(seg.gps_start_lat);
    const sLng = parseFloat(seg.gps_start_lng);
    const eLat = parseFloat(seg.gps_end_lat);
    const eLng = parseFloat(seg.gps_end_lng);
    const THRESHOLD = 0.0003;
    const startDist = Math.sqrt((sLat - handhole.lat) ** 2 + (sLng - handhole.lng) ** 2);
    const endDist = Math.sqrt((eLat - handhole.lat) ** 2 + (eLng - handhole.lng) ** 2);
    return startDist <= THRESHOLD || endDist <= THRESHOLD;
  });

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      background: darkMode ? '#0d1b2a' : '#ffffff',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: sizeColor, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: text }}>{label}</div>
            <div style={{ fontSize: 11, color: textMuted }}>Handhole</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
          <X size={20} />
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>
        <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <MapPin size={15} color={accent} />
            <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Details</span>
          </div>
          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.8 }}>
            <div><strong style={{ color: text }}>ID:</strong> {handhole.id || handhole.device_id || label}</div>
            <div><strong style={{ color: text }}>Size:</strong> {sizeLabel}</div>
            <div><strong style={{ color: text }}>Unit Code:</strong> {code}</div>
            <div><strong style={{ color: text }}>GPS:</strong> {handhole.lat?.toFixed(6)}, {handhole.lng?.toFixed(6)}</div>
          </div>
        </div>

        {connected.length > 0 && (
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Navigation size={15} color={accent} />
              <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Connected Segments ({connected.length})</span>
            </div>
            {connected.map(seg => (
              <div key={seg.segment_id} style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 6,
                background: darkMode ? '#0a1628' : '#fff', border: `1px solid ${borderColor}`,
                fontSize: 12, color: textMuted,
              }}>
                <div style={{ fontWeight: 600, color: text }}>{seg.contractor_id}</div>
                <div>{seg.footage} LF • {seg.street || '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== FLOWERPOT DETAIL PANEL =====
function FlowerpotDetailPanel({ flowerpot, segments, darkMode, onClose, user, onFlowerpotUpdate }) {
  if (!flowerpot) return null;

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const label = flowerpot.label || 'Flowerpot';
  const lat = parseFloat(flowerpot.gps_lat);
  const lng = parseFloat(flowerpot.gps_lng);

  // Find connected segments
  const connected = (segments || []).filter(seg => {
    const sLat = parseFloat(seg.gps_start_lat);
    const sLng = parseFloat(seg.gps_start_lng);
    const eLat = parseFloat(seg.gps_end_lat);
    const eLng = parseFloat(seg.gps_end_lng);
    const THRESHOLD = 0.0003;
    const startDist = Math.sqrt((sLat - lat) ** 2 + (sLng - lng) ** 2);
    const endDist = Math.sqrt((eLat - lat) ** 2 + (eLng - lng) ** 2);
    return startDist <= THRESHOLD || endDist <= THRESHOLD;
  });

  const handleInstallStatusChange = async (statusData) => {
    const deviceId = flowerpot.device_id;
    const newStatus = statusData.value;
    console.log(`[ProjectMap] FP status: ${deviceId}.status = ${newStatus}`);

    const ok = await updateDeviceField(deviceId, 'status', newStatus);
    if (ok) {
      await logAction(flowerpot.project_id || null, deviceId, user?.email, 'flowerpot_status_changed', {
        from: flowerpot.status, to: newStatus, notes: statusData.notes,
      });
      if (statusData.notes) {
        await updateDeviceField(deviceId, 'notes', statusData.notes);
      }
      if (onFlowerpotUpdate) onFlowerpotUpdate(deviceId, 'status', newStatus);
      console.log(`[ProjectMap] FP updated: ${deviceId}.status = ${newStatus}`);
    } else {
      console.error(`[ProjectMap] FP write failed: ${deviceId}.status`);
    }
  };

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      background: darkMode ? '#0d1b2a' : '#ffffff',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 12, height: 12, background: '#9C27B0', border: '2px solid #fff', transform: 'rotate(45deg)', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: text }}>{label}</div>
            <div style={{ fontSize: 11, color: textMuted }}>Flowerpot / Utility Box</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
          <X size={20} />
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>
        <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Flower2 size={15} color="#9C27B0" />
            <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Details</span>
          </div>
          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.8 }}>
            {flowerpot.device_id && <div><strong style={{ color: text }}>ID:</strong> {flowerpot.device_id}</div>}
            <div><strong style={{ color: text }}>Type:</strong> {flowerpot.vendor_codes?.[0]?.description || 'Utility Box (UG12)'}</div>
            <div><strong style={{ color: text }}>Unit Code:</strong> {flowerpot.vendor_codes?.[0]?.code || flowerpot.unit_code || 'UG12'}</div>
            {flowerpot.crew_name && <div><strong style={{ color: text }}>Crew:</strong> {flowerpot.crew_name}</div>}
            {flowerpot.section && <div><strong style={{ color: text }}>Section:</strong> {flowerpot.section}</div>}
            {!isNaN(lat) && <div><strong style={{ color: text }}>GPS:</strong> {lat.toFixed(6)}, {lng.toFixed(6)}</div>}
          </div>
        </div>

        {/* Install workflow tracker */}
        <FlowerpotInstallTracker
          flowerpot={flowerpot}
          darkMode={darkMode}
          user={user}
          onStatusUpdate={handleInstallStatusChange}
        />

        {connected.length > 0 && (
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Navigation size={15} color={accent} />
              <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Connected Segments ({connected.length})</span>
            </div>
            {connected.map(seg => (
              <div key={seg.segment_id} style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 6,
                background: darkMode ? '#0a1628' : '#fff', border: `1px solid ${borderColor}`,
                fontSize: 12, color: textMuted,
              }}>
                <div style={{ fontWeight: 600, color: text }}>{seg.contractor_id}</div>
                <div>{seg.footage} LF • {seg.street || '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== GENERIC DEVICE DETAIL PANEL (Pedestal, Cabinet) =====
function DeviceDetailPanel({ device, deviceType, segments, darkMode, onClose }) {
  if (!device) return null;

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const isPedestal = deviceType === 'pedestal';
  const iconColor = isPedestal ? '#00897B' : '#B71C1C';
  const typeLabel = isPedestal ? 'Pedestal' : 'Cabinet';
  const lat = parseFloat(device.gps_lat);
  const lng = parseFloat(device.gps_lng);

  const primaryCode = device.vendor_codes?.[0];
  const codeLabel = primaryCode?.description || typeLabel;
  const unitCode = primaryCode?.code || device.unit_code || '';

  // Find connected segments
  const connected = (segments || []).filter(seg => {
    const sLat = parseFloat(seg.gps_start_lat);
    const sLng = parseFloat(seg.gps_start_lng);
    const eLat = parseFloat(seg.gps_end_lat);
    const eLng = parseFloat(seg.gps_end_lng);
    const THRESHOLD = 0.0003;
    const startDist = Math.sqrt((sLat - lat) ** 2 + (sLng - lng) ** 2);
    const endDist = Math.sqrt((eLat - lat) ** 2 + (eLng - lng) ** 2);
    return startDist <= THRESHOLD || endDist <= THRESHOLD;
  });

  // Parse vendor_codes if stored as JSON string
  let vendorCodes = device.vendor_codes;
  if (typeof vendorCodes === 'string') {
    try { vendorCodes = JSON.parse(vendorCodes); } catch { vendorCodes = null; }
  }

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      background: darkMode ? '#0d1b2a' : '#ffffff',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: isPedestal ? 12 : 12, height: isPedestal ? 12 : 12,
            background: iconColor, border: `2px solid #fff`,
            transform: isPedestal ? 'rotate(45deg)' : 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: text }}>{device.device_id || typeLabel}</div>
            <div style={{ fontSize: 11, color: textMuted }}>{typeLabel}</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
          <X size={20} />
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>
        <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <MapPin size={15} color={accent} />
            <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Details</span>
          </div>
          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.8 }}>
            <div><strong style={{ color: text }}>ID:</strong> {device.device_id || '—'}</div>
            <div><strong style={{ color: text }}>Type:</strong> {codeLabel}</div>
            {unitCode && <div><strong style={{ color: text }}>Unit Code:</strong> {unitCode}</div>}
            <div><strong style={{ color: text }}>Status:</strong> {device.status || 'Not Started'}</div>
            {device.crew_name && <div><strong style={{ color: text }}>Crew:</strong> {device.crew_name}</div>}
            {device.section && <div><strong style={{ color: text }}>Section:</strong> {device.section}</div>}
            {!isNaN(lat) && <div><strong style={{ color: text }}>GPS:</strong> {lat.toFixed(6)}, {lng.toFixed(6)}</div>}
          </div>
        </div>

        {vendorCodes && vendorCodes.length > 0 && (
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FileText size={15} color={accent} />
              <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Vendor Codes ({vendorCodes.length})</span>
            </div>
            {vendorCodes.map((vc, i) => (
              <div key={i} style={{
                padding: '6px 10px', marginBottom: 4, borderRadius: 6,
                background: darkMode ? '#0a1628' : '#fff', border: `1px solid ${borderColor}`,
                fontSize: 11, color: textMuted,
              }}>
                <div style={{ fontWeight: 600, color: text }}>{vc.code}{vc.is_primary ? ' ★' : ''}</div>
                {vc.description && <div>{vc.description}</div>}
                {(vc.estimated_qty > 0 || vc.actual_qty > 0) && (
                  <div>Est: {vc.estimated_qty} | Actual: {vc.actual_qty} {vc.uom}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {connected.length > 0 && (
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Navigation size={15} color={accent} />
              <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Connected Segments ({connected.length})</span>
            </div>
            {connected.map(seg => (
              <div key={seg.segment_id} style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 6,
                background: darkMode ? '#0a1628' : '#fff', border: `1px solid ${borderColor}`,
                fontSize: 12, color: textMuted,
              }}>
                <div style={{ fontWeight: 600, color: text }}>{seg.contractor_id}</div>
                <div>{seg.footage} LF • {seg.street || '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== BILLING CALCULATION HELPER =====
// Client billing rates — populated at runtime via fetchFullRates (key-gated,
// admin batch panel only). Never hardcoded in the bundle.
const RATES = {};
const HH_TYPE_MAP = { '15x20x12': 'UG20', '17x30x18': 'UG17', '24x36x18': 'UG18', '24x36x24': 'UG11', '30x48x18': 'UG19', '30x48x24': 'UG27', '30x48x30': 'UG10' };

function computeSelectionBilling(selectedItems) {
  const items = {}; // code -> { description, uom, qty, unitPrice, total }
  let totalFootage = 0;
  const deviceCounts = { splice_1x4: 0, splice_1x8: 0, splice_f1: 0, handhole: 0, flowerpot: 0 };

  function addItem(code, qty) {
    const rate = RATES[code];
    if (!rate || !qty) return;
    if (!items[code]) items[code] = { description: rate.description, uom: rate.uom, qty: 0, unitPrice: rate.price, total: 0 };
    items[code].qty += qty;
    items[code].total = Math.round(items[code].qty * items[code].unitPrice * 100) / 100;
  }

  selectedItems.forEach(item => {
    if (item.type === 'segment') {
      const seg = item.data;
      const footage = parseFloat(seg.boring_actual_footage) || parseFloat(seg.footage) || 0;
      totalFootage += footage;
      // Boring
      const ducts = parseInt(seg.duct_count) || 1;
      const boreCode = ducts >= 6 ? 'UG24' : ducts >= 5 ? 'UG23' : 'UG1';
      addItem(boreCode, footage);
      // Pulling
      const cableType = seg.pulling_cable_type || seg.cable_type || '';
      const ct = parseInt(cableType) || 0;
      addItem(ct > 144 ? 'UG28' : 'UG4', parseFloat(seg.footage) || 0);
    } else if (item.type === 'device') {
      const dev = item.data;
      const dt = item.deviceType || '';
      if (dt === 'splice') {
        const st = (dev.splice_type || '').toLowerCase().replace('2x8', '1x8');
        if (st === '1x4') {
          deviceCounts.splice_1x4++;
          addItem('FS4', 1); addItem('FS1', 2); addItem('FS3', 8);
        } else if (st === '1x8') {
          deviceCounts.splice_1x8++;
          addItem('FS4', 1); addItem('FS1', 8); addItem('FS3', 16);
        } else if (st === 'f1' || st === 'tyco-d') {
          deviceCounts.splice_f1++;
          addItem('FS4', 1); addItem('FS1', parseInt(dev.fiber_count) || 432);
        }
      } else if (dt === 'handhole') {
        deviceCounts.handhole++;
        const hhType = dev.type || dev.size || '';
        const hhCode = HH_TYPE_MAP[hhType] || dev.code || dev.unit_code || 'UG17';
        addItem(hhCode, 1);
      } else if (dt === 'flowerpot') {
        deviceCounts.flowerpot++;
        addItem('UG12', 1);
      }
    }
  });

  const grandTotal = Object.values(items).reduce((s, i) => s + i.total, 0);
  return { items, grandTotal: Math.round(grandTotal * 100) / 100, totalFootage, deviceCounts };
}

// ===== ADMIN BATCH PANEL =====
const PHASE_STATUSES = ['', 'Not Started', 'Potholing', 'Pothole Approved', 'In Progress', 'Complete', 'QC Approved'];
const DEVICE_STATUSES = ['', 'Not Started', 'In Progress', 'Complete', 'QC Approved'];
const BILLING_STATUSES = ['', 'Not Billed', 'Submitted', 'Approved', 'Paid'];

function AdminBatchPanel({ selectedItems, setSelectedItems, darkMode, adminPhaseStatus, setAdminPhaseStatus, adminDeviceStatus, setAdminDeviceStatus, adminBillingStatus, setAdminBillingStatus, adminInvoiceId, setAdminInvoiceId, adminApplyPhases, setAdminApplyPhases, adminSaving, onApplyStatus, onApplyBilling, onApplyLyt, onApplySection, onApplyProject, onSelectInvoice, onSelectAll, onClearSelection, onClose, existingSections, allProjectsList, currentProjectId, paymentInvoices, selectedInvoiceId, setSelectedInvoiceId, invoiceFetching, onRefreshInvoices, segments, allDevices, assignments, onAssign, drawRectMode, setDrawRectMode, onQuickStatus }) {
  const [adminPanelMode, setAdminPanelMode] = useState('status');
  const [assignWorkType, setAssignWorkType] = useState('boring');
  const [assignContractor, setAssignContractor] = useState('');
  const [assignEmail, setAssignEmail] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  const [onboardedContractors, setOnboardedContractors] = useState([]);
  const [, setRatesLoaded] = useState(false);

  // Populate module-level RATES from server (key-gated; this panel is admin-only)
  React.useEffect(() => {
    let live = true;
    fetchFullRates('vexus-la-tx-2026')
      .then(d => {
        if (!live || !d?.rates) return;
        const pf = d.priceField || 'vexus';
        Object.keys(RATES).forEach(k => delete RATES[k]);
        for (const [code, r] of Object.entries(d.rates)) {
          RATES[code] = { description: r.description, uom: r.uom, price: r[pf] || 0 };
        }
        setRatesLoaded(true);
      })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, []);

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = '#c850c0';

  // Fetch onboarded contractors from user database (Google Sheets)
  React.useEffect(() => {
    const USERS_CSV_URL = 'https://docs.google.com/spreadsheets/d/1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw/gviz/tq?tqx=out:csv';
    (async () => {
      try {
        const resp = await fetch(USERS_CSV_URL + '&_t=' + Date.now());
        const csvText = await resp.text();
        const lines = csvText.split('\n').filter(l => l.trim());
        if (lines.length < 2) return;
        const parseRow = (row) => {
          const vals = []; let cur = ''; let inQ = false;
          for (let i = 0; i < row.length; i++) {
            const c = row[i];
            if (c === '"') inQ = !inQ;
            else if (c === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
            else cur += c;
          }
          vals.push(cur.trim());
          return vals;
        };
        const headers = parseRow(lines[0]);
        const users = [];
        for (let i = 1; i < lines.length; i++) {
          const vals = parseRow(lines[i]);
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = vals[idx] || ''; });
          users.push(obj);
        }
        const contractors = users
          .filter(u => u.email && u.email.trim() && ['contractor', 'sub'].includes((u.role || '').toLowerCase()))
          .map(u => ({ name: u.name || u.company || u.email, email: u.email, company: u.company || '' }));
        console.log('[AdminBatchPanel] Onboarded contractors:', contractors.length, 'of', users.length, 'users. Roles found:', [...new Set(users.map(u => u.role))]);
        setOnboardedContractors(contractors);
      } catch (err) {
        console.warn('[AdminBatchPanel] Failed to load onboarded contractors:', err);
      }
    })();
  }, []);

  // Build contractor list: only portal users with contractor/sub role
  const knownContractors = React.useMemo(() => {
    return onboardedContractors
      .map(c => [c.company ? `${c.name} (${c.company})` : c.name, c.email])
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [onboardedContractors]);

  const handleAssign = async () => {
    const segIds = selectedItems.filter(i => i.type === 'segment').map(i => i.id);
    if (segIds.length === 0 || !assignContractor.trim()) return;
    setAssigning(true);
    setAssignResult(null);
    try {
      const result = await onAssign(segIds, assignWorkType, assignContractor.trim(), assignEmail.trim());
      setAssignResult(result);
    } catch (err) {
      setAssignResult({ success: false, error: err.message });
    } finally {
      setAssigning(false);
    }
  };

  const segCount = selectedItems.filter(i => i.type === 'segment').length;
  const devCount = selectedItems.filter(i => i.type === 'device').length;

  const selectStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${borderColor}`, background: cardBg, color: text, fontSize: 13, minHeight: 44 };
  const inputStyle = { ...selectStyle };

  const modes = [
    { key: 'status', label: 'Status', color: '#4CAF50' },
    { key: 'billing', label: 'Billing', color: '#2196F3' },
    { key: 'assign', label: 'Assign', color: '#FFB800' },
  ];

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: bg }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={18} color={accent} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: text }}>Admin Mode</div>
              <div style={{ fontSize: 11, color: textMuted }}>{segCount} seg, {devCount} dev selected</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Selection controls */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button onClick={onSelectAll} style={{ flex: 1, padding: '6px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: `1px solid ${borderColor}`, background: cardBg, color: text, cursor: 'pointer' }}>Select All</button>
          <button onClick={onClearSelection} style={{ flex: 1, padding: '6px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: `1px solid ${borderColor}`, background: cardBg, color: textMuted, cursor: 'pointer' }}>Clear</button>
        </div>

        {/* Mode switcher */}
        <div style={{ display: 'flex', gap: 4 }}>
          {modes.map(m => (
            <button key={m.key} onClick={() => setAdminPanelMode(m.key)} style={{
              flex: 1, padding: '10px 4px', fontSize: 13, fontWeight: 700, borderRadius: 10, border: 'none', cursor: 'pointer',
              background: adminPanelMode === m.key ? m.color : (darkMode ? '#112240' : '#f1f5f9'),
              color: adminPanelMode === m.key ? '#fff' : textMuted,
              transition: 'all 0.15s ease', minHeight: 44,
            }}>{m.label}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>

        {/* ========== STATUS MODE ========== */}
        {adminPanelMode === 'status' && (
          <>
            {/* Quick Status Buttons — PROMOTED to top, 64px like MapActionBar */}
            {selectedItems.length > 0 && segCount > 0 && onQuickStatus && (
              <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quick Status (All Phases)</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { label: 'In Progress', color: '#FFB800', icon: '\u25B6' },
                    { label: 'Complete', color: '#4CAF50', icon: '\u2713' },
                    { label: 'QC Approved', color: '#2196F3', icon: '\u2605' },
                    { label: 'Issue', color: '#FF5722', icon: '!' },
                  ].map(btn => (
                    <button key={btn.label} disabled={adminSaving}
                      onClick={() => onQuickStatus(btn.label)}
                      style={{
                        flex: 1, borderRadius: 16, border: 'none',
                        fontSize: '0.95rem', fontWeight: 800, cursor: adminSaving ? 'not-allowed' : 'pointer',
                        backgroundColor: btn.color, color: '#fff',
                        opacity: adminSaving ? 0.6 : 1, transition: 'all 0.15s',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 4,
                        height: 64,
                      }}>
                      <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{btn.icon}</span>
                      <span>{btn.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Per-Phase Status Override — collapsed by default */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
              <details>
                <summary style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', listStyle: 'none', WebkitAppearance: 'none' }}>
                  <CheckCircle size={15} color='#4CAF50' />
                  <span style={{ fontWeight: 600, color: text, fontSize: 13, flex: 1 }}>Per-Phase Status Override</span>
                  <span style={{ fontSize: 11, color: textMuted }}>expand</span>
                </summary>
                <div style={{ marginTop: 12 }}>
                  {segCount > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 8 }}>SEGMENT PHASES</div>
                      {['boring', 'pulling', 'splicing'].map(phase => (
                        <div key={phase} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <span style={{ fontSize: 13, color: text, width: 70, textTransform: 'capitalize', fontWeight: 500 }}>{phase}</span>
                          <select value={adminPhaseStatus[phase]} onChange={e => setAdminPhaseStatus(prev => ({ ...prev, [phase]: e.target.value }))} style={selectStyle}>
                            {PHASE_STATUSES.map(s => <option key={s} value={s}>{s || '— No change —'}</option>)}
                          </select>
                        </div>
                      ))}
                    </>
                  )}

                  {devCount > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 8, marginTop: segCount > 0 ? 12 : 0 }}>DEVICE STATUS</div>
                      <select value={adminDeviceStatus} onChange={e => setAdminDeviceStatus(e.target.value)} style={selectStyle}>
                        {DEVICE_STATUSES.map(s => <option key={s} value={s}>{s || '— No change —'}</option>)}
                      </select>
                    </>
                  )}

                  {selectedItems.length === 0 && (
                    <div style={{ padding: 12, borderRadius: 10, background: borderColor + '33', textAlign: 'center', fontSize: 13, color: textMuted }}>
                      Select segments or devices on the map
                    </div>
                  )}

                  {selectedItems.length > 0 && (
                    <button onClick={onApplyStatus} disabled={adminSaving} style={{ width: '100%', marginTop: 12, padding: '12px', fontSize: 13, fontWeight: 600, borderRadius: 10, border: 'none', background: '#4CAF50', color: '#fff', cursor: adminSaving ? 'not-allowed' : 'pointer', opacity: adminSaving ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44 }}>
                      <Save size={14} /> {adminSaving ? 'Saving...' : 'Apply Status'}
                    </button>
                  )}
                </div>
              </details>
            </div>

            {/* Selected items list */}
            {selectedItems.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 8 }}>SELECTED ITEMS</div>
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {selectedItems.map(item => (
                    <div key={`${item.type}-${item.id}`} style={{ padding: '8px 10px', marginBottom: 4, borderRadius: 8, background: cardBg, border: `1px solid ${borderColor}`, fontSize: 12, color: text, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{item.type === 'segment' ? (item.data.contractor_id || item.id) : (item.data.label || item.data.splice_id || item.id)}</span>
                      <span style={{ color: textMuted, textTransform: 'capitalize', fontSize: 11 }}>{item.type === 'device' ? (item.deviceType || 'device') : 'segment'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Draw Rectangle select tool */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginTop: 12, border: `1px solid ${borderColor}` }}>
              <button
                onClick={() => setDrawRectMode && setDrawRectMode(!drawRectMode)}
                style={{
                  width: '100%', padding: 12, fontSize: 13, fontWeight: 600,
                  borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: drawRectMode ? '#FFB80020' : 'transparent',
                  border: `1px ${drawRectMode ? 'solid' : 'dashed'} ${drawRectMode ? '#FFB800' : borderColor}`,
                  color: drawRectMode ? '#FFB800' : textMuted, minHeight: 44,
                }}
              >
                {drawRectMode ? 'Drawing... (drag on map)' : 'Draw Rectangle'}
              </button>
            </div>
          </>
        )}

        {/* ========== BILLING MODE ========== */}
        {adminPanelMode === 'billing' && (
          <>
            {/* Invoice selector + billing status */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <DollarSign size={15} color='#2196F3' />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Batch Billing</span>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>INVOICE</div>
                <select value={selectedInvoiceId} onChange={e => { const val = e.target.value; setSelectedInvoiceId(val); const inv = (paymentInvoices || []).find(i => i.invoice_id === val); setAdminInvoiceId(inv ? (inv.invoice_number || inv.invoice_id) : ''); if (onSelectInvoice) onSelectInvoice(inv); }} style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${borderColor}`, background: cardBg, color: text, fontSize: 12 }}>
                  <option value="">— No invoice selected —</option>
                  {(paymentInvoices || []).filter(inv => !currentProjectId || inv.project_id === currentProjectId).map(inv => (
                    <option key={inv.invoice_id} value={inv.invoice_id}>
                      {inv.invoice_number || inv.invoice_id} — ${parseFloat(inv.net_amount || inv.subtotal || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scan Drive / Upload */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button onClick={() => onRefreshInvoices('scan')} disabled={invoiceFetching} style={{ flex: 1, padding: '6px', fontSize: 10, fontWeight: 600, borderRadius: 6, border: `1px solid ${borderColor}`, background: invoiceFetching ? borderColor : '#FF6B3522', color: invoiceFetching ? textMuted : '#FF6B35', cursor: invoiceFetching ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <RefreshCw size={11} style={invoiceFetching ? { animation: 'mapSpin 1s linear infinite' } : {}} />
                  {invoiceFetching ? 'Scanning...' : 'Scan Drive'}
                </button>
                <label style={{ padding: '6px 8px', fontSize: 10, fontWeight: 600, borderRadius: 6, border: `1px solid ${borderColor}`, background: cardBg, color: textMuted, cursor: invoiceFetching ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Upload size={11} /> Upload
                  <input type="file" accept=".pdf" style={{ display: 'none' }} disabled={invoiceFetching} onChange={(e) => onRefreshInvoices('upload', e)} />
                </label>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>BILLING STATUS</div>
                <select value={adminBillingStatus} onChange={e => setAdminBillingStatus(e.target.value)} style={selectStyle}>
                  {BILLING_STATUSES.map(s => <option key={s} value={s}>{s || '— Select —'}</option>)}
                </select>
              </div>

              {segCount > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>APPLY TO PHASES</div>
                  {['boring', 'pulling', 'splicing'].map(phase => (
                    <label key={phase} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 12, color: text, cursor: 'pointer' }}>
                      <input type="checkbox" checked={adminApplyPhases[phase]} onChange={e => setAdminApplyPhases(prev => ({ ...prev, [phase]: e.target.checked }))} />
                      <span style={{ textTransform: 'capitalize' }}>{phase}</span>
                    </label>
                  ))}
                </div>
              )}

              <button onClick={onApplyBilling} disabled={adminSaving || !adminBillingStatus} style={{ width: '100%', marginTop: 4, padding: '10px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: 'none', background: '#2196F3', color: '#fff', cursor: (adminSaving || !adminBillingStatus) ? 'not-allowed' : 'pointer', opacity: (adminSaving || !adminBillingStatus) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <DollarSign size={14} /> {adminSaving ? 'Saving...' : 'Apply Billing'}
              </button>
            </div>

            {/* Selection Billing Summary */}
            {selectedItems.length > 0 && (() => {
              const billing = computeSelectionBilling(selectedItems);
              const entries = Object.entries(billing.items);
              return (
                <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <FileText size={15} color={accent} />
                    <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Selection Summary</span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {billing.totalFootage > 0 && (
                      <span style={{ padding: '3px 8px', borderRadius: 12, background: accent + '22', color: accent, fontSize: 11, fontWeight: 600 }}>
                        {billing.totalFootage.toLocaleString()} LF
                      </span>
                    )}
                    {billing.deviceCounts.splice_1x4 > 0 && <span style={{ padding: '3px 8px', borderRadius: 12, background: '#4CAF5022', color: '#4CAF50', fontSize: 11, fontWeight: 600 }}>{billing.deviceCounts.splice_1x4} x 1x4</span>}
                    {billing.deviceCounts.splice_1x8 > 0 && <span style={{ padding: '3px 8px', borderRadius: 12, background: '#2196F322', color: '#2196F3', fontSize: 11, fontWeight: 600 }}>{billing.deviceCounts.splice_1x8} x 1x8</span>}
                    {billing.deviceCounts.splice_f1 > 0 && <span style={{ padding: '3px 8px', borderRadius: 12, background: '#FF6B3522', color: '#FF6B35', fontSize: 11, fontWeight: 600 }}>{billing.deviceCounts.splice_f1} x F1</span>}
                    {billing.deviceCounts.handhole > 0 && <span style={{ padding: '3px 8px', borderRadius: 12, background: '#9C27B022', color: '#9C27B0', fontSize: 11, fontWeight: 600 }}>{billing.deviceCounts.handhole} HH</span>}
                    {billing.deviceCounts.flowerpot > 0 && <span style={{ padding: '3px 8px', borderRadius: 12, background: '#FF980022', color: '#FF9800', fontSize: 11, fontWeight: 600 }}>{billing.deviceCounts.flowerpot} FP</span>}
                  </div>

                  {entries.length > 0 && (
                    <div style={{ maxHeight: 160, overflow: 'auto', marginBottom: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <th style={{ textAlign: 'left', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Code</th>
                            <th style={{ textAlign: 'left', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Description</th>
                            <th style={{ textAlign: 'right', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Qty</th>
                            <th style={{ textAlign: 'right', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(([code, li]) => (
                            <tr key={code} style={{ borderBottom: `1px solid ${borderColor}22` }}>
                              <td style={{ padding: '3px 4px', color: accent, fontWeight: 600 }}>{code}</td>
                              <td style={{ padding: '3px 4px', color: text, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{li.description}</td>
                              <td style={{ padding: '3px 4px', color: text, textAlign: 'right' }}>{li.qty.toLocaleString()} {li.uom}</td>
                              <td style={{ padding: '3px 4px', color: text, textAlign: 'right' }}>${li.total.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: `2px solid ${accent}`, fontWeight: 700, fontSize: 13 }}>
                    <span style={{ color: text }}>Grand Total</span>
                    <span style={{ color: accent }}>${billing.grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              );
            })()}

            {/* Invoice Comparison */}
            {selectedInvoiceId && (
              <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <FileText size={15} color='#FF6B35' />
                  <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Invoice Comparison</span>
                </div>

                {selectedItems.length > 0 && (() => {
                  const billing = computeSelectionBilling(selectedItems);
                  const invoice = (paymentInvoices || []).find(inv => inv.invoice_id === selectedInvoiceId);
                  if (!invoice) return null;
                  const invoiceItems = (() => {
                    try { return JSON.parse(invoice.line_items_json || '[]'); } catch { return []; }
                  })();

                  const allCodes = new Set([
                    ...Object.keys(billing.items),
                    ...invoiceItems.map(li => (li.code || '').replace(/^([A-Z]+)0+(\d+)/, '$1$2')),
                  ]);
                  let matchCount = 0, mismatchCount = 0, missingCount = 0;
                  const rows = [...allCodes].sort().map(code => {
                    const expected = billing.items[code] || null;
                    const invItem = invoiceItems.find(li => (li.code || '').replace(/^([A-Z]+)0+(\d+)/, '$1$2') === code);
                    const expectedQty = expected ? expected.qty : 0;
                    const invoiceQty = invItem ? (invItem.quantity || 0) : 0;
                    let status = 'match';
                    if (expectedQty > 0 && invoiceQty === 0) { status = 'missing'; missingCount++; }
                    else if (invoiceQty > 0 && expectedQty === 0) { status = 'extra'; mismatchCount++; }
                    else if (Math.abs(expectedQty - invoiceQty) > 0.01) { status = 'mismatch'; mismatchCount++; }
                    else { matchCount++; }
                    return { code, expected, invItem, expectedQty, invoiceQty, status };
                  });

                  const discrepancyTotal = mismatchCount + missingCount;
                  const statusColors = { match: '#4CAF50', mismatch: '#f44336', missing: '#FF9800', extra: '#9C27B0' };

                  return (
                    <div>
                      {discrepancyTotal > 0 ? (
                        <div style={{ padding: '8px 10px', borderRadius: 8, background: '#f4433622', border: '1px solid #f44336', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AlertTriangle size={16} color='#f44336' />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#f44336' }}>
                            {discrepancyTotal} discrepanc{discrepancyTotal === 1 ? 'y' : 'ies'} found
                          </span>
                        </div>
                      ) : (
                        <div style={{ padding: '8px 10px', borderRadius: 8, background: '#4CAF5022', border: '1px solid #4CAF50', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle size={16} color='#4CAF50' />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#4CAF50' }}>All items match</span>
                        </div>
                      )}

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, fontSize: 10 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4CAF50', display: 'inline-block' }} /> Match</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f44336', display: 'inline-block' }} /> Mismatch</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF9800', display: 'inline-block' }} /> Missing</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#9C27B0', display: 'inline-block' }} /> Extra</span>
                      </div>

                      <div style={{ maxHeight: 200, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                              <th style={{ textAlign: 'left', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Code</th>
                              <th style={{ textAlign: 'right', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Selected</th>
                              <th style={{ textAlign: 'right', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Invoice</th>
                              <th style={{ textAlign: 'center', padding: '3px 4px', color: textMuted, fontWeight: 600 }}>Diff</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map(row => (
                              <tr key={row.code} style={{ borderBottom: `1px solid ${borderColor}22`, background: row.status !== 'match' ? statusColors[row.status] + '11' : 'transparent' }}>
                                <td style={{ padding: '3px 4px', fontWeight: 600, color: statusColors[row.status] }}>{row.code}</td>
                                <td style={{ padding: '3px 4px', textAlign: 'right', color: text }}>{row.expectedQty || '—'}</td>
                                <td style={{ padding: '3px 4px', textAlign: 'right', color: text }}>{row.invoiceQty || '—'}</td>
                                <td style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: statusColors[row.status] }}>
                                  {row.status === 'match' ? '✓' : row.status === 'missing' ? 'MISSING' : row.status === 'extra' ? 'EXTRA' : (row.invoiceQty - row.expectedQty > 0 ? '+' : '') + (row.invoiceQty - row.expectedQty)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: 8, padding: '6px 0', borderTop: `1px solid ${borderColor}`, fontSize: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ color: textMuted }}>Selected Total:</span>
                          <span style={{ color: text, fontWeight: 600 }}>${billing.grandTotal.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: textMuted }}>Invoice Total:</span>
                          <span style={{ color: text, fontWeight: 600 }}>${parseFloat(invoice.net_amount || invoice.subtotal || 0).toLocaleString()}</span>
                        </div>
                        {Math.abs(billing.grandTotal - parseFloat(invoice.net_amount || invoice.subtotal || 0)) > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, padding: '4px 0', borderTop: `1px solid #f44336` }}>
                            <span style={{ color: '#f44336', fontWeight: 600 }}>Difference:</span>
                            <span style={{ color: '#f44336', fontWeight: 700 }}>${Math.abs(billing.grandTotal - parseFloat(invoice.net_amount || invoice.subtotal || 0)).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {selectedItems.length === 0 && (
                  <div style={{ padding: 10, borderRadius: 8, background: borderColor + '33', textAlign: 'center', fontSize: 12, color: textMuted }}>
                    Select segments and devices on the map to compare against this invoice
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ========== ASSIGN MODE ========== */}
        {adminPanelMode === 'assign' && (() => {
          const lytSegCount = segments.filter(s => s.lyt_assigned).length;
          const lytDevCount = (allDevices || []).filter(d => d.lyt_assigned).length;
          const totalSegCount = segments.length;
          const totalDevCount = (allDevices || []).length;
          const totalItems = segCount + devCount;
          // Group segments + devices by section for quick assign
          const sectionCounts = {};
          segments.forEach(s => {
            const sec = s.section || 'Unknown';
            if (!sectionCounts[sec]) sectionCounts[sec] = { totalSeg: 0, lytSeg: 0, totalDev: 0, lytDev: 0 };
            sectionCounts[sec].totalSeg++;
            if (s.lyt_assigned) sectionCounts[sec].lytSeg++;
          });
          (allDevices || []).forEach(d => {
            const sec = d.section || 'Unknown';
            if (!sectionCounts[sec]) sectionCounts[sec] = { totalSeg: 0, lytSeg: 0, totalDev: 0, lytDev: 0 };
            sectionCounts[sec].totalDev++;
            if (d.lyt_assigned) sectionCounts[sec].lytDev++;
          });
          const sectionList = Object.entries(sectionCounts).sort((a, b) => a[0].localeCompare(b[0]));

          return (
          <>
            {/* LYT Assignment */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Shield size={15} color='#f97316' />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>LYT Work Assignment</span>
              </div>

              {/* LYT Status Summary */}
              <div style={{ padding: 10, borderRadius: 8, background: '#f9731610', border: '1px solid #f9731640', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f97316' }}>{lytSegCount}/{totalSegCount} seg, {lytDevCount}/{totalDevCount} dev assigned</div>
                <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>Only LYT-assigned items are visible to workers</div>
              </div>

              {totalItems === 0 && (
                <div style={{ padding: 10, borderRadius: 8, background: borderColor + '33', textAlign: 'center', fontSize: 12, color: textMuted }}>
                  Select segments or devices on the map
                </div>
              )}

              {totalItems > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 6 }}>
                    {segCount > 0 && `${segCount} seg`}{segCount > 0 && devCount > 0 && ', '}{devCount > 0 && `${devCount} dev`} selected
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => onApplyLyt(true)} disabled={adminSaving} style={{
                      flex: 1, padding: '10px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: 'none',
                      background: '#f97316', color: '#fff', cursor: adminSaving ? 'not-allowed' : 'pointer',
                      opacity: adminSaving ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                      <CheckCircle size={14} /> {adminSaving ? 'Saving...' : 'Assign to LYT'}
                    </button>
                    <button onClick={() => onApplyLyt(false)} disabled={adminSaving} style={{
                      flex: 1, padding: '10px', fontSize: 12, fontWeight: 600, borderRadius: 8,
                      border: `1px solid ${borderColor}`, background: 'transparent', color: textMuted,
                      cursor: adminSaving ? 'not-allowed' : 'pointer', opacity: adminSaving ? 0.6 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                      <X size={14} /> {adminSaving ? 'Saving...' : 'Remove'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Section-level quick assign */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 8 }}>SECTIONS</div>
              {sectionList.map(([sec, counts]) => {
                const totalUnassigned = (counts.totalSeg - counts.lytSeg) + (counts.totalDev - counts.lytDev);
                const totalAssigned = counts.lytSeg + counts.lytDev;
                const total = counts.totalSeg + counts.totalDev;
                return (
                <div key={sec} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${borderColor}22` }}>
                  <div>
                    <div style={{ fontSize: 12, color: text, fontWeight: 500 }}>{sec.replace('BASTROP_', '')}</div>
                    <div style={{ fontSize: 10, color: textMuted }}>{totalAssigned}/{total} assigned</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {totalUnassigned > 0 && (
                      <button onClick={() => {
                        const sectionSegs = segments.filter(s => s.section === sec && !s.lyt_assigned);
                        const sectionDevs = (allDevices || []).filter(d => d.section === sec && !d.lyt_assigned);
                        const items = [
                          ...sectionSegs.map(s => ({ type: 'segment', id: s.segment_id, data: s })),
                          ...sectionDevs.map(d => ({ type: 'device', id: d.device_id || d.splice_id || d.id, data: d })),
                        ];
                        setSelectedItems(prev => {
                          const existing = new Set(prev.map(i => i.id));
                          return [...prev, ...items.filter(i => !existing.has(i.id))];
                        });
                      }} style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4, border: `1px solid #f9731660`, background: '#f9731610', color: '#f97316', cursor: 'pointer' }}>
                        + Select {totalUnassigned}
                      </button>
                    )}
                    {totalAssigned > 0 && (
                      <span style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, color: '#10b981' }}>✓ {totalAssigned}</span>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            {/* Crew Assignment */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <HardHat size={15} color='#FFB800' />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Crew Assignment</span>
              </div>

              {segCount > 0 ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>WORK TYPE</div>
                    <select value={assignWorkType} onChange={e => setAssignWorkType(e.target.value)} style={selectStyle}>
                      <option value="boring">Boring</option>
                      <option value="pulling">Fiber Pulling</option>
                      <option value="splicing">Splicing</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>CONTRACTOR / CREW</div>
                    <select
                      value={assignContractor}
                      onChange={e => {
                        const name = e.target.value;
                        setAssignContractor(name);
                        const match = knownContractors.find(([n]) => n === name);
                        if (match && match[1]) setAssignEmail(match[1]);
                      }}
                      style={selectStyle}
                    >
                      <option value="">Select contractor...</option>
                      {knownContractors.map(([name, email]) => (
                        <option key={name} value={name}>{name}{email ? ` — ${email}` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>EMAIL (optional)</div>
                    <input
                      type="email" value={assignEmail}
                      onChange={e => setAssignEmail(e.target.value)}
                      placeholder="contractor@email.com"
                      style={inputStyle}
                    />
                    <div style={{ fontSize: 10, color: textMuted, marginTop: 2 }}>Auto-filled from contractor record</div>
                  </div>

                  <button
                    onClick={handleAssign}
                    disabled={!assignContractor.trim() || assigning}
                    style={{
                      width: '100%', padding: 10, fontSize: 12, fontWeight: 600,
                      borderRadius: 8, border: 'none', cursor: !assignContractor.trim() || assigning ? 'not-allowed' : 'pointer',
                      background: !assignContractor.trim() || assigning ? '#6c757d' : '#FFB800',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      opacity: !assignContractor.trim() || assigning ? 0.6 : 1,
                    }}
                  >
                    <HardHat size={14} />
                    {assigning ? 'Assigning...' : `Assign ${segCount} Segment${segCount !== 1 ? 's' : ''}`}
                  </button>

                  {assignResult && (
                    <div style={{
                      marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12,
                      background: assignResult.success ? '#4CAF5015' : assignResult.updated > 0 ? '#FFB80015' : '#FF444415',
                      border: `1px solid ${assignResult.success ? '#4CAF5044' : assignResult.updated > 0 ? '#FFB80044' : '#FF444444'}`,
                      color: assignResult.success ? '#4CAF50' : assignResult.updated > 0 ? '#FFB800' : '#FF4444',
                    }}>
                      {assignResult.success
                        ? `Assigned ${assignResult.updated} segments to ${assignContractor}`
                        : assignResult.updated > 0
                          ? `${assignResult.updated}/${assignResult.total} assigned`
                          : assignResult.error || 'Assignment failed'}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: 10, borderRadius: 8, background: borderColor + '33', textAlign: 'center', fontSize: 12, color: textMuted }}>
                  Select segments to assign crews
                </div>
              )}
            </div>

            {/* Draw Rectangle select tool */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, border: `1px solid ${borderColor}` }}>
              <div style={{ fontSize: 11, color: textMuted, marginBottom: 8, textAlign: 'center' }}>or select by area</div>
              <button
                onClick={() => setDrawRectMode && setDrawRectMode(!drawRectMode)}
                style={{
                  width: '100%', padding: 12, fontSize: 13, fontWeight: 600,
                  borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: drawRectMode ? '#FFB80020' : 'transparent',
                  border: `1px ${drawRectMode ? 'solid' : 'dashed'} ${drawRectMode ? '#FFB800' : borderColor}`,
                  color: drawRectMode ? '#FFB800' : textMuted, minHeight: 44,
                }}
              >
                {drawRectMode ? 'Drawing... (drag on map)' : 'Draw Rectangle'}
              </button>
            </div>

            {/* Section Assignment (merged from Organize tab) */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginTop: 12, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Users size={15} color='#FF6B35' />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Section Assignment</span>
              </div>
              {segCount > 0 ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>ASSIGN TO SECTION</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="text" id="admin-section-input" placeholder="e.g. 1, 2, A..." style={{ ...inputStyle, flex: 1 }} list="section-suggestions" />
                      <datalist id="section-suggestions">
                        {(existingSections || []).map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                  </div>
                  <button onClick={() => { const val = document.getElementById('admin-section-input')?.value; if (val && onApplySection) onApplySection(val); }} disabled={adminSaving} style={{ width: '100%', marginTop: 4, padding: '12px', fontSize: 13, fontWeight: 600, borderRadius: 10, border: 'none', background: '#FF6B35', color: '#fff', cursor: adminSaving ? 'not-allowed' : 'pointer', opacity: adminSaving ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44 }}>
                    <Users size={14} /> {adminSaving ? 'Saving...' : 'Assign Section'}
                  </button>
                </>
              ) : (
                <div style={{ padding: 12, borderRadius: 10, background: borderColor + '33', textAlign: 'center', fontSize: 13, color: textMuted }}>
                  Select segments on the map to assign sections
                </div>
              )}
            </div>

            {/* Project Reassignment (merged from Organize tab) */}
            <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginTop: 12, border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Navigation size={15} color='#FF6B35' />
                <span style={{ fontWeight: 600, color: text, fontSize: 13 }}>Reassign to Project</span>
              </div>
              {selectedItems.length > 0 && (allProjectsList || []).length > 1 ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 4 }}>TARGET PROJECT</div>
                    <select id="admin-reassign-project" style={selectStyle}>
                      <option value="">— Select project —</option>
                      {(allProjectsList || []).filter(p => p.id !== currentProjectId).map(p => (
                        <option key={p.id} value={p.id}>{p.name || p.id}</option>
                      ))}
                    </select>
                  </div>
                  <button onClick={() => { const val = document.getElementById('admin-reassign-project')?.value; if (val && onApplyProject) onApplyProject(val); }} disabled={adminSaving} style={{ width: '100%', marginTop: 4, padding: '12px', fontSize: 13, fontWeight: 600, borderRadius: 10, border: 'none', background: '#e53e3e', color: '#fff', cursor: adminSaving ? 'not-allowed' : 'pointer', opacity: adminSaving ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44 }}>
                    <Navigation size={14} /> {adminSaving ? 'Moving...' : `Reassign ${selectedItems.length} Item${selectedItems.length > 1 ? 's' : ''}`}
                  </button>
                </>
              ) : (
                <div style={{ padding: 12, borderRadius: 10, background: borderColor + '33', textAlign: 'center', fontSize: 13, color: textMuted }}>
                  {(allProjectsList || []).length <= 1 ? 'Only one project exists' : 'Select items on the map to reassign'}
                </div>
              )}
            </div>
          </>
          );
        })()}

      </div>
    </div>
  );
}

// ===== MAIN COMPONENT =====
function ProjectMapPage({ darkMode, setDarkMode, user, setCurrentPage, projectId }) {
  const _saved = loadMapState();
  const [filterSection, setFilterSection] = useState('all'); // restored after sections load
  const [filterStatus, setFilterStatus] = useState(_saved.filterStatus || 'all');
  const [filterPhase, setFilterPhase] = useState(_saved.filterPhase || 'boring');
  const [filterCrew, setFilterCrew] = useState('all'); // 'all', 'lyt', or a contractor name
  const [showAllVitruvi, setShowAllVitruvi] = useState(false); // admin-only: show full Vitruvi dump
  const [viewMode, setViewMode] = useState('construction'); // 'construction' or 'vitruvi'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedSplice, setSelectedSplice] = useState(null);
  const [selectedHandhole, setSelectedHandhole] = useState(null);
  const [selectedFlowerpot, setSelectedFlowerpot] = useState(null);
  const [selectedPedestal, setSelectedPedestal] = useState(null);
  const [selectedCabinet, setSelectedCabinet] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLayerToggles, setShowLayerToggles] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [mapMenuOpen, setMapMenuOpen] = useState(false);

  // Contractors only see status-colored segments + aerial by default (less clutter)
  const _isContractor = user?.role === 'Contractor' || user?.role === 'contractor';
  const _contractorDefault = (key, adminDefault) => {
    if (_saved[key] !== undefined) return _saved[key];
    return _isContractor ? false : adminDefault;
  };

  // Layer visibility toggles (restore from saved state)
  const [showFlowerpots, setShowFlowerpots] = useState(_contractorDefault('showFlowerpots', true));
  const [showSplicePoints, setShowSplicePoints] = useState(_contractorDefault('showSplicePoints', true));
  const [showHandholes, setShowHandholes] = useState(_contractorDefault('showHandholes', true));
  const [showPedestals, setShowPedestals] = useState(_contractorDefault('showPedestals', true));
  const [showCabinets, setShowCabinets] = useState(_contractorDefault('showCabinets', true));
  const [showAerial, setShowAerial] = useState(_saved.showAerial !== undefined ? _saved.showAerial : true);
  const [showFats, setShowFats] = useState(_contractorDefault('showFats', true));
  const [colorblindMode, setColorblindMode] = useState(_saved.colorblindMode || false);

  // Map preset state
  const [mapPresets, setMapPresets] = useState(DEFAULT_MAP_PRESETS);
  const [activePreset, setActivePreset] = useState('construction');
  const [savingPreset, setSavingPreset] = useState(false);

  // Project selector state
  const [allProjectsList, setAllProjectsList] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(projectId || _saved.currentProjectId || 'BASTROP');

  // Section data (loaded independently for dropdown)
  const [availableSections, setAvailableSections] = useState([]);

  // Dynamic data state - billable items (filtered by section when selected)
  const [allSegments, setAllSegments] = useState([]);
  const [splicePoints, setSplicePoints] = useState([]);
  const [handholes, setHandholes] = useState([]);
  const [flowerpots, setFlowerpots] = useState([]);
  const [pedestals, setPedestals] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [fats, setFats] = useState([]);
  const [groundRods, setGroundRods] = useState([]);
  const [locates, setLocates] = useState([]);
  const [damageReports, setDamageReports] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState('loading');

  const [showVersion, setShowVersion] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [lastRefresh, setLastRefresh] = useState(null);

  const { showToast, ToastContainer } = useToast();
  const isAdmin = user?.role === 'Admin' || user?.role === 'admin' || user?.role === 'Supervisor' || user?.role === 'QC Inspector';
  const isContractor = user?.role === 'Contractor' || user?.role === 'contractor';

  // Admin batch mode state
  const [adminMode, setAdminMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminPhaseStatus, setAdminPhaseStatus] = useState({ boring: '', pulling: '', splicing: '' });
  const [adminDeviceStatus, setAdminDeviceStatus] = useState('');
  const [adminBillingStatus, setAdminBillingStatus] = useState('');
  const [adminInvoiceId, setAdminInvoiceId] = useState('');
  const [adminApplyPhases, setAdminApplyPhases] = useState({ boring: false, pulling: false, splicing: false });

  // Rectangle draw mode for area-select
  const [drawRectMode, setDrawRectMode] = useState(false);

  // Map Notes state
  const [mapNotes, setMapNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [gpsOn, setGpsOn] = useState(false);

  // Invoice verification state
  const [paymentInvoices, setPaymentInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [invoiceFetching, setInvoiceFetching] = useState(false);

  // Load available projects list on mount
  useEffect(() => {
    async function loadProjects() {
      try {
        const projects = await loadProjectsList();
        // Hide completed projects — only show active builds
        const HIDDEN_PREFIXES = ['SLPH', 'METRO'];
        const valid = projects.filter(p => p.id && p.id !== 'OFFLINE' && !HIDDEN_PREFIXES.some(pfx => p.id.startsWith(pfx)));
        setAllProjectsList(valid);
        // Default to BASTROP if no project selected
        if (!currentProjectId && valid.length > 0) {
          setCurrentProjectId(valid.find(p => p.id === 'BASTROP')?.id || valid[0].id);
        }
      } catch (err) {
        console.warn('[ProjectMap] Project list load:', err);
      }
    }
    loadProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync if parent changes projectId
  useEffect(() => {
    if (projectId && projectId !== currentProjectId) {
      setCurrentProjectId(projectId);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sections list when project changes (lightweight query for dropdown)
  // Contractors only see sections where they have assigned segments
  const contractorCompany = isContractor && user?.company ? user.company : null;
  useEffect(() => {
    if (!currentProjectId) return;
    let cancelled = false;
    async function fetchSections() {
      try {
        const secs = await loadSections(currentProjectId, contractorCompany);
        if (!cancelled) {
          setAvailableSections(secs);
          // Restore saved section if it exists in fetched list
          const saved = loadMapState();
          const savedSection = saved.filterSection;
          if (savedSection && savedSection !== 'all' && secs.some(s => s.section === savedSection) && saved.currentProjectId === currentProjectId) {
            setFilterSection(savedSection);
          } else if (contractorCompany && secs.length === 1) {
            // Contractor with exactly one section — auto-select it
            setFilterSection(secs[0].section);
          } else {
            // Auto-select first section for very large non-BASTROP projects
            const totalItems = secs.reduce((sum, s) => sum + (parseInt(s.count) || 0), 0);
            if (currentProjectId === 'BASTROP') {
              // Show all sections for BASTROP — Vitruvi shows everything, so should we
              setFilterSection('all');
            } else if (totalItems > 5000 && secs.length > 1) {
              setFilterSection(secs[0].section);
            } else {
              setFilterSection('all');
            }
          }
        }
      } catch (err) {
        console.warn('[ProjectMap] Sections load:', err);
      }
    }
    fetchSections();
    return () => { cancelled = true; };
  }, [currentProjectId, contractorCompany]);

  // Load map presets from DB
  useEffect(() => {
    if (!currentProjectId) return;
    (async () => {
      const rows = await loadMapPresets(currentProjectId);
      if (rows && rows.length > 0) {
        const merged = { ...DEFAULT_MAP_PRESETS };
        rows.forEach(r => {
          if (r.preset_name && r.config) {
            merged[r.preset_name] = typeof r.config === 'string' ? JSON.parse(r.config) : r.config;
          }
        });
        setMapPresets(merged);
      }
    })();
  }, [currentProjectId]);

  // Apply preset config to existing state setters
  const applyPreset = useCallback((name) => {
    const cfg = mapPresets[name] || DEFAULT_MAP_PRESETS[name];
    if (!cfg) return;
    setActivePreset(name);
    setFilterPhase(cfg.phase || 'boring');
    setShowAllVitruvi(cfg.lytOnly === false);
    setShowHandholes(cfg.showHandholes !== false);
    setShowFlowerpots(cfg.showFlowerpots !== false);
    setShowSplicePoints(cfg.showSplicePoints !== false);
    setShowPedestals(cfg.showPedestals !== false);
    setShowCabinets(cfg.showCabinets !== false);
    setShowFats(cfg.showFats !== false);
    setShowAerial(cfg.showAerial !== false);
  }, [mapPresets]);

  // No auto-apply on ProjectMapPage — localStorage already restores the admin's saved state.
  // Presets only apply when the user explicitly clicks a preset button.

  // Save current state as a map preset
  const handleSavePreset = useCallback(async () => {
    if (!currentProjectId || savingPreset) return;
    setSavingPreset(true);
    // Phase always matches the preset name to prevent mismatches
    const presetPhase = activePreset === 'construction' ? 'boring' : activePreset;
    const config = {
      phase: presetPhase,
      filterStatus: 'all',
      lytOnly: !showAllVitruvi,
      showHandholes,
      showFlowerpots,
      showSplicePoints,
      showPedestals,
      showCabinets,
      showFats,
      showAerial,
      showNotes,
    };
    const result = await saveMapPreset(currentProjectId, activePreset, config, user?.email || 'unknown');
    if (result.success) {
      setMapPresets(prev => ({ ...prev, [activePreset]: config }));
      showToast('Preset saved', 'success');
    } else {
      showToast('Failed to save preset', 'error');
    }
    setSavingPreset(false);
  }, [currentProjectId, savingPreset, filterPhase, showAllVitruvi, showHandholes, showFlowerpots, showSplicePoints, showPedestals, showCabinets, showFats, showAerial, showNotes, activePreset, user, showToast]);

  // Load project data when project or section filter changes + auto-refresh every 30s
  useEffect(() => {
    if (!currentProjectId) return;
    let cancelled = false;
    let refreshInterval = null;

    async function fetchData(silent = false) {
      if (!silent) setLoading(true);
      try {
        const role = (user?.role || '').toLowerCase();
        const adminUser = role === 'admin' || role === 'supervisor' || role === 'qc inspector';
        const sectionParam = filterSection !== 'all' ? filterSection : undefined;
        const data = adminUser ? await loadFullProjectWithLineItems(currentProjectId, sectionParam) : await loadFullProject(currentProjectId, sectionParam);
        if (!cancelled) {
          setAllSegments(data.segments);
          setSplicePoints(data.splicePoints || []);
          setHandholes(data.handholes || []);
          setFlowerpots(data.flowerpots || []);
          setPedestals(data.pedestals || []);
          setCabinets(data.cabinets || []);
          setFats(data.fats || []);
          setGroundRods(data.groundRods || []);
          setProjectAssignments(data.assignments || []);
          // Load utility locates for this project
          try {
            const locData = await loadLocates(currentProjectId || data.project?.project_id || null);
            setLocates(locData);
          } catch (locErr) { console.warn('[ProjectMap] Locates load:', locErr); }
          try {
            const dmgData = await loadDamageReports(currentProjectId || data.project?.project_id || null);
            setDamageReports(dmgData);
          } catch (dmgErr) { console.warn('[ProjectMap] Damage reports load:', dmgErr); }
          // Load map notes
          try {
            const notesData = await loadMapNotes(currentProjectId, sectionParam);
            if (!cancelled) setMapNotes(notesData || []);
          } catch (notesErr) { console.warn('[ProjectMap] Notes load:', notesErr); }
          // Load payment invoices for invoice verification (admin only, non-blocking)
          if (adminUser) {
            loadEmailInvoices().then(inv => {
              if (!cancelled) setPaymentInvoices(inv || []);
            }).catch(err => console.warn('[ProjectMap] Invoices load:', err));
          }
          setProjectInfo(data.project);
          setDataSource(data.isDemo ? 'demo' : 'live');
          setLastRefresh(new Date());
          if (!silent) setLoading(false);
          console.log(`[ProjectMap] Loaded: ${data.segments.length} seg, ${(data.handholes || []).length} HH, ${(data.flowerpots || []).length} FP, ${(data.pedestals || []).length} Ped, ${(data.cabinets || []).length} Cab, ${(data.splicePoints || []).length} SP${sectionParam ? ` [section: ${sectionParam}]` : ' [all sections]'}`);
        }
      } catch (err) {
        console.error('[ProjectMapPage] Data load failed:', err);
        if (!cancelled && !silent) { setLoading(false); setDataSource('demo'); }
      }
    }

    fetchData(false);

    // Auto-refresh every 30 seconds (silent - no loading spinner)
    refreshInterval = setInterval(() => {
      if (!cancelled) fetchData(true);
    }, 30000);

    return () => { cancelled = true; if (refreshInterval) clearInterval(refreshInterval); };
  }, [currentProjectId, filterSection]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Persist map state to localStorage
  useEffect(() => {
    localStorage.setItem(MAP_STATE_KEY, JSON.stringify({
      currentProjectId, filterSection, filterPhase, filterStatus,
      showFlowerpots, showSplicePoints, showHandholes, showPedestals, showCabinets, showAerial, showFats,
      colorblindMode,
    }));
  }, [currentProjectId, filterSection, filterPhase, filterStatus, showFlowerpots, showSplicePoints, showHandholes, showPedestals, showCabinets, showAerial, showFats, colorblindMode]);

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  // Base segment visibility:
  // - Contractors: only their assigned work
  // - Admin with "Show all Vitruvi" ON: everything
  // - Everyone else: only segments marked as lyt_assigned
  const visibleSegments = useMemo(() => {
    if (isContractor && user?.company) {
      const company = (user.company || '').toLowerCase();
      return allSegments.filter(seg => {
        const boringMatch = (seg.boring_assigned_to || '').toLowerCase().includes(company);
        const pullingMatch = (seg.pulling_assigned_to || '').toLowerCase().includes(company);
        const splicingMatch = (seg.splicing_assigned_to || '').toLowerCase().includes(company);
        return boringMatch || pullingMatch || splicingMatch;
      });
    }
    if (showAllVitruvi) return allSegments;
    // Default: only show segments marked as LYT-assigned
    return allSegments.filter(seg => seg.lyt_assigned === true);
  }, [allSegments, isContractor, user, showAllVitruvi]);

  // Filter devices by lyt_assigned (same logic as segments)
  const visibleSplicePoints = useMemo(() => showAllVitruvi ? splicePoints : splicePoints.filter(d => d.lyt_assigned === true), [splicePoints, showAllVitruvi]);
  const visibleHandholes = useMemo(() => showAllVitruvi ? handholes : handholes.filter(d => d.lyt_assigned === true), [handholes, showAllVitruvi]);
  const visibleFlowerpots = useMemo(() => showAllVitruvi ? flowerpots : flowerpots.filter(d => d.lyt_assigned === true), [flowerpots, showAllVitruvi]);
  const visiblePedestals = useMemo(() => showAllVitruvi ? pedestals : pedestals.filter(d => d.lyt_assigned === true), [pedestals, showAllVitruvi]);
  const visibleCabinets = useMemo(() => showAllVitruvi ? cabinets : cabinets.filter(d => d.lyt_assigned === true), [cabinets, showAllVitruvi]);
  const visibleFats = useMemo(() => showAllVitruvi ? fats : fats.filter(d => d.lyt_assigned === true), [fats, showAllVitruvi]);

  // Build unique crew/contractor names for the active phase's assigned_to field
  const availableCrews = useMemo(() => {
    const assignedField = filterPhase === 'boring' ? 'boring_assigned_to' : filterPhase === 'pulling' ? 'pulling_assigned_to' : 'splicing_assigned_to';
    const names = new Set();
    for (const seg of visibleSegments) {
      const val = (seg[assignedField] || '').trim();
      if (val) names.add(val);
    }
    return [...names].sort();
  }, [visibleSegments, filterPhase]);

  // Reset crew filter when phase changes (contractor names differ per phase)
  useEffect(() => { setFilterCrew('all'); }, [filterPhase]);

  // Filter segments (section, status, phase, crew — search is now highlight-only, not filter)
  const filteredSegments = useMemo(() => {
    const assignedField = filterPhase === 'boring' ? 'boring_assigned_to' : filterPhase === 'pulling' ? 'pulling_assigned_to' : 'splicing_assigned_to';
    return visibleSegments.filter(seg => {
      if (filterSection !== 'all' && seg.section !== filterSection) return false;
      if (filterStatus !== 'all') {
        const status = filterPhase === 'boring' ? seg.boring_status : filterPhase === 'pulling' ? seg.pulling_status : seg.splicing_status;
        if (status !== filterStatus) return false;
      }
      // Crew filter: 'lyt' = unassigned (empty assigned_to), or match a specific contractor name
      if (filterCrew !== 'all') {
        const assigned = (seg[assignedField] || '').trim();
        if (filterCrew === 'lyt') {
          if (assigned !== '') return false;
        } else {
          if (assigned !== filterCrew) return false;
        }
      }
      return true;
    });
  }, [visibleSegments, filterSection, filterStatus, filterPhase, filterCrew]);

  // Search match IDs — used for highlight dimming on map (null = no search active)
  const searchMatchIds = useMemo(() => {
    if (!searchTerm) return null;
    const search = searchTerm.toLowerCase();
    const ids = new Set();
    filteredSegments.forEach(seg => {
      if ((seg.contractor_id || '').toLowerCase().includes(search) ||
          (seg.street || '').toLowerCase().includes(search) ||
          (seg.segment_id || '').toLowerCase().includes(search) ||
          (seg.from_handhole || '').toLowerCase().includes(search) ||
          (seg.to_handhole || '').toLowerCase().includes(search) ||
          (seg.bore_code || '').toLowerCase().includes(search) ||
          (seg.section || '').toLowerCase().includes(search)) {
        ids.add(seg.segment_id);
      }
    });
    return ids;
  }, [filteredSegments, searchTerm]);

  // Stats (based on visible segments for role-appropriate counts)
  const stats = useMemo(() => {
    const totalFootage = visibleSegments.reduce((sum, s) => sum + (parseFloat(s.footage) || 0), 0);
    const phaseKey = filterPhase === 'boring' ? 'boring_status' : filterPhase === 'pulling' ? 'pulling_status' : 'splicing_status';
    const approved = visibleSegments.filter(s => s[phaseKey] === 'QC Approved').length;
    const complete = visibleSegments.filter(s => s[phaseKey] === 'Complete').length;
    const inProgress = visibleSegments.filter(s => s[phaseKey] === 'In Progress').length;
    const issues = visibleSegments.filter(s => s[phaseKey] === 'Issue').length;
    const notStarted = visibleSegments.filter(s => s[phaseKey] === 'Not Started').length;

    // ALL billable item counts
    const handholesCount = visibleHandholes.length;
    const flowerpotCount = visibleFlowerpots.length;
    const pedestalCount = visiblePedestals.length;
    const cabinetCount = visibleCabinets.length;
    const groundRodCount = groundRods.length;
    const fatCount = visibleFats.length;
    const splice1x4 = visibleSplicePoints.filter(sp => sp.splice_type === '1x4').length;
    const splice2x8 = visibleSplicePoints.filter(sp => sp.splice_type === '2x8' || sp.splice_type === '1x8').length;

    return {
      totalFootage, approved, complete, inProgress, issues, notStarted,
      total: visibleSegments.length,
      handholesCount, flowerpotCount, pedestalCount, cabinetCount, groundRodCount, fatCount,
      splice1x4, splice2x8,
    };
  }, [visibleSegments, filterPhase, visibleSplicePoints, visibleHandholes, visibleFlowerpots, visiblePedestals, visibleCabinets, groundRods, visibleFats]);

  const clearSelections = useCallback(() => {
    setSelectedSegment(null); setSelectedSplice(null); setSelectedHandhole(null);
    setSelectedFlowerpot(null); setSelectedPedestal(null); setSelectedCabinet(null);
  }, []);

  const handleSelectSegment = useCallback((seg) => {
    clearSelections();
    setSelectedSegment(prev => prev && prev.segment_id === seg.segment_id ? null : seg);
  }, [clearSelections]);

  const handleSelectSplice = useCallback((sp) => {
    clearSelections();
    setSelectedSplice(prev => prev && prev.splice_id === sp.splice_id ? null : sp);
  }, [clearSelections]);

  const handleSelectHandhole = useCallback((hh) => {
    clearSelections();
    setSelectedHandhole(prev => prev && prev.lat === hh.lat && prev.lng === hh.lng ? null : hh);
  }, [clearSelections]);

  const handleSelectFlowerpot = useCallback((fp) => {
    clearSelections();
    setSelectedFlowerpot(prev => {
      const fpLat = parseFloat(fp.gps_lat);
      const fpLng = parseFloat(fp.gps_lng);
      return prev && parseFloat(prev.gps_lat) === fpLat && parseFloat(prev.gps_lng) === fpLng ? null : fp;
    });
  }, [clearSelections]);

  const handleSelectPedestal = useCallback((ped) => {
    clearSelections();
    setSelectedPedestal(prev => {
      const pLat = parseFloat(ped.gps_lat);
      const pLng = parseFloat(ped.gps_lng);
      return prev && parseFloat(prev.gps_lat) === pLat && parseFloat(prev.gps_lng) === pLng ? null : ped;
    });
  }, [clearSelections]);

  const handleSelectCabinet = useCallback((cab) => {
    clearSelections();
    setSelectedCabinet(prev => {
      const cLat = parseFloat(cab.gps_lat);
      const cLng = parseFloat(cab.gps_lng);
      return prev && parseFloat(prev.gps_lat) === cLat && parseFloat(prev.gps_lng) === cLng ? null : cab;
    });
  }, [clearSelections]);

  // Admin multi-select handlers
  const handleAdminSelectSegment = useCallback((seg) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.type === 'segment' && i.id === seg.segment_id);
      if (exists) return prev.filter(i => !(i.type === 'segment' && i.id === seg.segment_id));
      return [...prev, { type: 'segment', id: seg.segment_id, data: seg }];
    });
  }, []);

  const handleAdminSelectDevice = useCallback((device, deviceType) => {
    const id = device.device_id || device.splice_id || device.id || `${device.gps_lat || device.lat}-${device.gps_lng || device.lng}`;
    setSelectedItems(prev => {
      const exists = prev.find(i => i.type === 'device' && i.id === id);
      if (exists) return prev.filter(i => !(i.type === 'device' && i.id === id));
      return [...prev, { type: 'device', id, deviceType, data: device }];
    });
  }, []);

  const adminSelectedSegmentIds = useMemo(() => new Set(selectedItems.filter(i => i.type === 'segment').map(i => i.id)), [selectedItems]);
  const adminSelectedDeviceIds = useMemo(() => new Set(selectedItems.filter(i => i.type === 'device').map(i => i.id)), [selectedItems]);

  const handleSpliceUpdate = async (spliceId, field, value) => {
    console.log(`[ProjectMap] Splice update: ${spliceId}.${field}`);
    const ok = await updateSpliceField(spliceId, field, value);
    if (ok) {
      // Update local state
      setSplicePoints(prev => prev.map(sp =>
        sp.splice_id === spliceId ? { ...sp, [field]: value } : sp
      ));
      if (selectedSplice && selectedSplice.splice_id === spliceId) {
        setSelectedSplice(prev => ({ ...prev, [field]: value }));
      }
      console.log(`[ProjectMap] ✅ Splice updated: ${spliceId}.${field}`);
    }
    return ok;
  };

  // Admin batch apply handlers
  const handleAdminSelectAll = useCallback(() => {
    const segItems = filteredSegments.map(seg => ({ type: 'segment', id: seg.segment_id, data: seg }));
    const devItems = [];
    if (showSplicePoints && visibleSplicePoints) visibleSplicePoints.forEach(sp => {
      const id = sp.device_id || sp.splice_id || `${sp.gps_lat}-${sp.gps_lng}`;
      devItems.push({ type: 'device', id, deviceType: 'splice', data: sp });
    });
    if (showFlowerpots && visibleFlowerpots) visibleFlowerpots.forEach(fp => {
      const id = fp.device_id || `${fp.gps_lat}-${fp.gps_lng}`;
      devItems.push({ type: 'device', id, deviceType: 'flowerpot', data: fp });
    });
    if (showHandholes && visibleHandholes) visibleHandholes.forEach(hh => {
      const id = hh.device_id || hh.id || `${hh.lat}-${hh.lng}`;
      devItems.push({ type: 'device', id, deviceType: 'handhole', data: hh });
    });
    setSelectedItems([...segItems, ...devItems]);
  }, [filteredSegments, visibleSplicePoints, visibleFlowerpots, visibleHandholes, showSplicePoints, showFlowerpots, showHandholes]);

  const handleApplyBatchStatus = async () => {
    if (selectedItems.length === 0) return;
    setAdminSaving(true);
    try {
      const batchSegs = selectedItems.filter(i => i.type === 'segment');
      const batchDevs = selectedItems.filter(i => i.type === 'device');
      const now = new Date().toISOString();
      const who = user?.name || user?.email || 'admin';
      let updated = 0;

      for (const item of batchSegs) {
        for (const phase of ['boring', 'pulling', 'splicing']) {
          const newStatus = adminPhaseStatus[phase];
          if (!newStatus) continue;
          const field = `${phase}_status`;
          await updateSegmentField(item.id, field, newStatus);
          if (newStatus === 'In Progress') {
            const startField = phase === 'boring' ? 'boring_started' : phase === 'pulling' ? 'pulling_started' : 'started';
            await updateSegmentField(item.id, startField, now);
          }
          if (newStatus === 'Complete' || newStatus === 'QC Approved') {
            const tsField = phase === 'boring' ? 'boring_completed' : phase === 'pulling' ? 'pulling_completed' : 'completed';
            await updateSegmentField(item.id, tsField, now);
          }
          if (newStatus === 'QC Approved') {
            const qcBy = phase === 'boring' ? 'boring_qc_approved_by' : phase === 'pulling' ? 'pulling_qc_approved_by' : 'qc_approved_by';
            const qcDate = phase === 'boring' ? 'boring_qc_approved_date' : phase === 'pulling' ? 'pulling_qc_approved_date' : 'qc_approved_date';
            await updateSegmentField(item.id, qcBy, who);
            await updateSegmentField(item.id, qcDate, now);
          }
        }
        await logAction(currentProjectId, item.id, user?.email, 'admin_batch_override', { phases: adminPhaseStatus });
        updated++;
      }
      // Update segment local state
      setAllSegments(prev => prev.map(s => {
        const match = batchSegs.find(i => i.id === s.segment_id);
        if (!match) return s;
        const u = { ...s };
        for (const phase of ['boring', 'pulling', 'splicing']) {
          if (adminPhaseStatus[phase]) u[`${phase}_status`] = adminPhaseStatus[phase];
        }
        return u;
      }));

      for (const item of batchDevs) {
        if (adminDeviceStatus) {
          await updateDeviceField(item.id, 'status', adminDeviceStatus);
          await logAction(currentProjectId, item.id, user?.email, 'admin_batch_override', { status: adminDeviceStatus });
          updated++;
        }
      }
      // Update device local state
      if (adminDeviceStatus) {
        const devIds = new Set(batchDevs.map(d => d.id));
        setSplicePoints(prev => prev.map(sp => devIds.has(sp.device_id || sp.splice_id) ? { ...sp, status: adminDeviceStatus } : sp));
        setFlowerpots(prev => prev.map(fp => devIds.has(fp.device_id) ? { ...fp, status: adminDeviceStatus } : fp));
        setHandholes(prev => prev.map(hh => devIds.has(hh.device_id || hh.id) ? { ...hh, status: adminDeviceStatus } : hh));
      }

      setSelectedItems([]);
      setAdminPhaseStatus({ boring: '', pulling: '', splicing: '' });
      setAdminDeviceStatus('');
      showToast(`Status updated for ${updated} items`, 'success');
    } catch (err) {
      console.error('[AdminBatch] Status apply error:', err);
      showToast('Failed to apply status — check console', 'error');
    }
    setAdminSaving(false);
  };

  // Quick status apply — sets all phases + devices to the same status in one tap
  const handleQuickStatus = async (status) => {
    if (selectedItems.length === 0) return;
    setAdminSaving(true);
    try {
      const batchSegs = selectedItems.filter(i => i.type === 'segment');
      const batchDevs = selectedItems.filter(i => i.type === 'device');
      const now = new Date().toISOString();
      const who = user?.name || user?.email || 'admin';
      let updated = 0;

      for (const item of batchSegs) {
        for (const phase of ['boring', 'pulling', 'splicing']) {
          const field = `${phase}_status`;
          await updateSegmentField(item.id, field, status);
          if (status === 'In Progress') {
            await updateSegmentField(item.id, `${phase}_started`, now);
          }
          if (status === 'Complete' || status === 'QC Approved') {
            await updateSegmentField(item.id, `${phase}_completed`, now);
          }
          if (status === 'QC Approved') {
            await updateSegmentField(item.id, `${phase}_qc_approved_by`, who);
            await updateSegmentField(item.id, `${phase}_qc_approved_date`, now);
          }
        }
        await logAction(currentProjectId, item.id, user?.email, 'admin_quick_status', { status });
        updated++;
      }
      setAllSegments(prev => prev.map(s => {
        if (!batchSegs.find(i => i.id === s.segment_id)) return s;
        const u = { ...s };
        for (const phase of ['boring', 'pulling', 'splicing']) {
          u[`${phase}_status`] = status;
        }
        return u;
      }));

      if (status !== 'Issue') {
        for (const item of batchDevs) {
          await updateDeviceField(item.id, 'status', status);
          updated++;
        }
        const devIds = new Set(batchDevs.map(d => d.id));
        if (devIds.size > 0) {
          setSplicePoints(prev => prev.map(sp => devIds.has(sp.device_id || sp.splice_id) ? { ...sp, status } : sp));
          setFlowerpots(prev => prev.map(fp => devIds.has(fp.device_id) ? { ...fp, status } : fp));
          setHandholes(prev => prev.map(hh => devIds.has(hh.device_id || hh.id) ? { ...hh, status } : hh));
          setPedestals && setPedestals(prev => prev.map(p => devIds.has(p.device_id) ? { ...p, status } : p));
          setCabinets && setCabinets(prev => prev.map(c => devIds.has(c.device_id) ? { ...c, status } : c));
        }
      }

      setSelectedItems([]);
      setAdminPhaseStatus({ boring: '', pulling: '', splicing: '' });
      setAdminDeviceStatus('');
      showToast(`${updated} items → ${status}`, 'success');
    } catch (err) {
      console.error('[AdminBatch] Quick status error:', err);
      showToast('Failed to apply status — check console', 'error');
    }
    setAdminSaving(false);
  };

  const handleApplyBatchBilling = async () => {
    if (selectedItems.length === 0 || !adminBillingStatus) return;
    setAdminSaving(true);
    try {
      const batchSegs = selectedItems.filter(i => i.type === 'segment');
      const batchDevs = selectedItems.filter(i => i.type === 'device');
      let updated = 0;

      for (const item of batchSegs) {
        for (const phase of ['boring', 'pulling', 'splicing']) {
          if (!adminApplyPhases[phase]) continue;
          await updateSegmentField(item.id, `${phase}_billing_status`, adminBillingStatus);
          if (adminInvoiceId) await updateSegmentField(item.id, `${phase}_submission_id`, adminInvoiceId);
        }
        await logAction(currentProjectId, item.id, user?.email, 'admin_batch_billing', { billing: adminBillingStatus, invoice: adminInvoiceId, phases: adminApplyPhases });
        updated++;
      }
      // Update segment local state
      const segIds = new Set(batchSegs.map(i => i.id));
      setAllSegments(prev => prev.map(s => {
        if (!segIds.has(s.segment_id)) return s;
        const u = { ...s };
        for (const phase of ['boring', 'pulling', 'splicing']) {
          if (adminApplyPhases[phase]) {
            u[`${phase}_billing_status`] = adminBillingStatus;
            if (adminInvoiceId) u[`${phase}_submission_id`] = adminInvoiceId;
          }
        }
        return u;
      }));

      for (const item of batchDevs) {
        await updateDeviceField(item.id, 'billing_status', adminBillingStatus);
        if (adminInvoiceId) await updateDeviceField(item.id, 'submission_id', adminInvoiceId);
        await logAction(currentProjectId, item.id, user?.email, 'admin_batch_billing', { billing: adminBillingStatus, invoice: adminInvoiceId });
        updated++;
      }
      // Update device local state
      const devIds = new Set(batchDevs.map(d => d.id));
      setSplicePoints(prev => prev.map(sp => {
        const id = sp.device_id || sp.splice_id;
        if (!devIds.has(id)) return sp;
        return { ...sp, billing_status: adminBillingStatus, ...(adminInvoiceId ? { submission_id: adminInvoiceId } : {}) };
      }));
      setFlowerpots(prev => prev.map(fp => {
        if (!devIds.has(fp.device_id)) return fp;
        return { ...fp, billing_status: adminBillingStatus, ...(adminInvoiceId ? { submission_id: adminInvoiceId } : {}) };
      }));
      setHandholes(prev => prev.map(hh => {
        if (!devIds.has(hh.device_id || hh.id)) return hh;
        return { ...hh, billing_status: adminBillingStatus, ...(adminInvoiceId ? { submission_id: adminInvoiceId } : {}) };
      }));

      setSelectedItems([]);
      setAdminBillingStatus('');
      setAdminInvoiceId('');
      showToast(`Billing updated for ${updated} items`, 'success');
    } catch (err) {
      console.error('[AdminBatch] Billing apply error:', err);
      showToast('Failed to apply billing — check console', 'error');
    }
    setAdminSaving(false);
  };

  const handleApplyBatchLyt = async (value) => {
    const batchSegs = selectedItems.filter(i => i.type === 'segment');
    const batchDevs = selectedItems.filter(i => i.type === 'device');
    if (batchSegs.length === 0 && batchDevs.length === 0) {
      showToast('No items selected — click segments or devices on the map first', 'error');
      return;
    }
    setAdminSaving(true);
    try {
      let updated = 0;
      const BATCH = 20;
      // Update segments in parallel batches
      for (let i = 0; i < batchSegs.length; i += BATCH) {
        const chunk = batchSegs.slice(i, i + BATCH);
        const results = await Promise.all(
          chunk.map(item => updateSegmentField(item.id, 'lyt_assigned', value))
        );
        updated += results.filter(Boolean).length;
      }
      // Update devices in parallel batches
      for (let i = 0; i < batchDevs.length; i += BATCH) {
        const chunk = batchDevs.slice(i, i + BATCH);
        const results = await Promise.all(
          chunk.map(item => updateDeviceField(item.id, 'lyt_assigned', value))
        );
        updated += results.filter(Boolean).length;
      }
      logAction(currentProjectId, null, user?.email, 'admin_batch_lyt_assign', { value, segments: batchSegs.length, devices: batchDevs.length, count: updated }).catch(() => {});
      // Update local segment state
      const segIds = new Set(batchSegs.map(i => i.id));
      setAllSegments(prev => prev.map(s => segIds.has(s.segment_id) ? { ...s, lyt_assigned: value } : s));
      // Update local device state
      const devIds = new Set(batchDevs.map(d => d.id));
      setSplicePoints(prev => prev.map(sp => {
        const id = sp.device_id || sp.splice_id;
        if (!devIds.has(id)) return sp;
        return { ...sp, lyt_assigned: value };
      }));
      setFlowerpots(prev => prev.map(fp => {
        if (!devIds.has(fp.device_id)) return fp;
        return { ...fp, lyt_assigned: value };
      }));
      setHandholes(prev => prev.map(hh => {
        if (!devIds.has(hh.device_id || hh.id)) return hh;
        return { ...hh, lyt_assigned: value };
      }));
      setPedestals(prev => prev.map(p => {
        if (!devIds.has(p.device_id)) return p;
        return { ...p, lyt_assigned: value };
      }));
      setCabinets(prev => prev.map(c => {
        if (!devIds.has(c.device_id)) return c;
        return { ...c, lyt_assigned: value };
      }));
      setSelectedItems([]);
      const parts = [];
      if (batchSegs.length > 0) parts.push(`${batchSegs.length} segments`);
      if (batchDevs.length > 0) parts.push(`${batchDevs.length} devices`);
      showToast(`${parts.join(' + ')} ${value ? 'assigned to' : 'removed from'} LYT (${updated} updated)`, 'success');
    } catch (err) {
      console.error('[AdminBatch] LYT assign error:', err);
      showToast(`LYT assign failed: ${err.message}`, 'error');
    }
    setAdminSaving(false);
  };

  const handleApplyBatchProject = async (targetProjectId) => {
    if (!targetProjectId || selectedItems.length === 0) return;
    setAdminSaving(true);
    try {
      const batchSegs = selectedItems.filter(i => i.type === 'segment');
      const batchDevs = selectedItems.filter(i => i.type === 'device');
      let updated = 0;

      for (const item of batchSegs) {
        await updateSegmentField(item.id, 'project_id', targetProjectId);
        updated++;
      }
      for (const item of batchDevs) {
        await updateDeviceField(item.id, 'project_id', targetProjectId);
        updated++;
      }
      await logAction(currentProjectId, null, user?.email, 'admin_batch_project_reassign', { target_project: targetProjectId, segments: batchSegs.length, devices: batchDevs.length });

      // Remove from local state — they no longer belong to this project
      const segIds = new Set(batchSegs.map(i => i.id));
      setAllSegments(prev => prev.filter(s => !segIds.has(s.segment_id)));
      const devIds = new Set(batchDevs.map(d => d.id));
      setSplicePoints(prev => prev.filter(sp => !devIds.has(sp.device_id || sp.splice_id)));
      setFlowerpots(prev => prev.filter(fp => !devIds.has(fp.device_id)));
      setHandholes(prev => prev.filter(hh => !devIds.has(hh.device_id || hh.id)));

      setSelectedItems([]);
      const targetName = (allProjectsList || []).find(p => p.id === targetProjectId)?.name || targetProjectId;
      showToast(`${updated} item${updated > 1 ? 's' : ''} moved to ${targetName}`, 'success');
    } catch (err) {
      console.error('[AdminBatch] Project reassign error:', err);
      showToast('Failed to reassign — check console', 'error');
    }
    setAdminSaving(false);
  };

  const handleApplyBatchSection = async (sectionValue) => {
    if (!sectionValue || selectedItems.length === 0) return;
    setAdminSaving(true);
    try {
      const batchSegs = selectedItems.filter(i => i.type === 'segment');
      const batchDevs = selectedItems.filter(i => i.type === 'device');
      let updated = 0;

      for (const item of batchSegs) {
        await updateSegmentField(item.id, 'section', sectionValue);
        updated++;
      }
      for (const item of batchDevs) {
        await updateDeviceField(item.id, 'section', sectionValue);
        updated++;
      }
      await logAction(currentProjectId, null, user?.email, 'admin_batch_section', { section: sectionValue, segments: batchSegs.length, devices: batchDevs.length });

      // Update local state
      const segIds = new Set(batchSegs.map(i => i.id));
      setAllSegments(prev => prev.map(s => segIds.has(s.segment_id) ? { ...s, section: sectionValue } : s));
      const devIds = new Set(batchDevs.map(d => d.id));
      setSplicePoints(prev => prev.map(sp => devIds.has(sp.device_id || sp.splice_id) ? { ...sp, section: sectionValue } : sp));
      setFlowerpots(prev => prev.map(fp => devIds.has(fp.device_id) ? { ...fp, section: sectionValue } : fp));
      setHandholes(prev => prev.map(hh => devIds.has(hh.device_id || hh.id) ? { ...hh, section: sectionValue } : hh));

      setSelectedItems([]);
      showToast(`Section "${sectionValue}" assigned to ${updated} items`, 'success');
      // Refresh sections list to pick up new section
      loadSections(currentProjectId).then(secs => setAvailableSections(secs)).catch(() => {});
    } catch (err) {
      console.error('[AdminBatch] Section assign error:', err);
      showToast('Failed to assign section — check console', 'error');
    }
    setAdminSaving(false);
  };

  // When an invoice is selected, auto-select all segments/devices already attached to it
  const handleSelectInvoice = useCallback((inv) => {
    if (!inv) { setSelectedItems([]); return; }
    const invLabel = inv.invoice_number || inv.invoice_id;
    const items = [];
    // Segments: check boring_submission_id and pulling_submission_id
    (allSegments || []).forEach(seg => {
      if (seg.boring_submission_id === invLabel || seg.pulling_submission_id === invLabel) {
        items.push({ type: 'segment', id: seg.segment_id, data: seg });
      }
    });
    // Devices: check submission_id
    (splicePoints || []).forEach(sp => {
      if (sp.submission_id === invLabel) {
        const id = sp.device_id || sp.splice_id;
        items.push({ type: 'device', id, deviceType: 'splice', data: sp });
      }
    });
    (flowerpots || []).forEach(fp => {
      if (fp.submission_id === invLabel) {
        items.push({ type: 'device', id: fp.device_id, deviceType: 'flowerpot', data: fp });
      }
    });
    (handholes || []).forEach(hh => {
      if (hh.submission_id === invLabel) {
        const id = hh.device_id || hh.id;
        items.push({ type: 'device', id, deviceType: 'handhole', data: hh });
      }
    });
    setSelectedItems(items);
  }, [allSegments, splicePoints, flowerpots, handholes]);

  // Invoice verification: scan Drive folder or upload PDF
  const handleRefreshInvoices = async (mode, e) => {
    setInvoiceFetching(true);
    try {
      if (mode === 'upload') {
        const file = e?.target?.files?.[0];
        if (!file) { setInvoiceFetching(false); return; }
        e.target.value = '';
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const result = await uploadInvoicePDF(currentProjectId, base64, file.name);
        if (result && result.success !== false) {
          showToast(`Parsed: ${result.data?.[0]?.invoice_number || 'OK'} (${result.data?.[0]?.line_item_count || 0} items)`, 'success');
        } else {
          showToast(result?.error || 'Invoice parse failed', 'error');
        }
      } else {
        // Scan Drive folder
        const result = await scanDriveInvoices(currentProjectId);
        if (result && result.success !== false) {
          const msg = [];
          if (result.processed > 0) msg.push(`${result.processed} imported`);
          if (result.skipped > 0) msg.push(`${result.skipped} skipped`);
          if (result.failed > 0) msg.push(`${result.failed} failed`);
          if (result.remaining > 0) msg.push(`${result.remaining} remaining`);
          showToast(msg.join(', ') || result.message || 'Scan complete', result.processed > 0 ? 'success' : 'info');
        } else {
          showToast(result?.error || 'Drive scan failed', 'error');
        }
      }
      // Reload the invoices list
      const inv = await loadEmailInvoices();
      setPaymentInvoices(inv || []);
    } catch (err) {
      console.error('[InvoiceVerify] Failed:', err);
      showToast('Invoice import failed — ' + err.message, 'error');
    }
    setInvoiceFetching(false);
  };

  const sections = availableSections.length > 0
    ? availableSections.map(s => s.section)
    : [...new Set(visibleSegments.map(s => s.section).filter(Boolean))].sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));

  return (
    <div style={{ minHeight: '100vh', maxWidth: '100vw', background: bg, color: text, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
      {/* Loading Overlay */}
      {loading && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 50, background: darkMode ? 'rgba(13,27,42,0.92)' : 'rgba(255,255,255,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${borderColor}`, borderTopColor: accent, borderRadius: '50%', animation: 'mapSpin 0.8s linear infinite' }} />
          <div style={{ color: textMuted, fontSize: 13 }}>Loading project data...</div>
          <style>{`@keyframes mapSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {/* Top Bar */}
      <div style={{
        padding: '12px 20px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        background: darkMode ? '#112240' : '#f8fafc', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setCurrentPage(isAdmin ? 'admin-projects' : (user?.role === 'Contractor' || user?.role === 'contractor') ? 'contractor-dashboard' : 'employee-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, display: 'flex' }}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              {allProjectsList.length > 1 ? (
                <select
                  value={currentProjectId || ''}
                  onChange={e => {
                    setCurrentProjectId(e.target.value);
                    clearSelections();
                    setSelectedInvoiceId(''); setAdminInvoiceId('');
                  }}
                  style={{
                    fontWeight: 700, fontSize: 16, border: 'none', background: 'transparent', color: text,
                    cursor: 'pointer', outline: 'none', maxWidth: isMobile ? 200 : 400,
                  }}
                >
                  {allProjectsList.map(p => (
                    <option key={p.id} value={p.id} style={{ background: darkMode ? '#112240' : '#fff', color: text }}>
                      {p.name || p.id}
                    </option>
                  ))}
                </select>
              ) : (
                <span>{projectInfo?.name || projectInfo?.project_name || 'Untitled Project'}</span>
              )}
              {isContractor && <span style={{ padding: '1px 6px', borderRadius: 4, background: '#667eea', color: '#fff', fontSize: 10, fontWeight: 600 }}>CONTRACTOR VIEW</span>}
            </div>
            {dataSource === 'demo' && (
              <div style={{ fontSize: 10 }}>
                <span style={{ padding: '1px 6px', borderRadius: 4, background: '#ff6b35', color: '#fff', fontWeight: 600 }}>DEMO</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isMobile ? (
            /* Mobile: hamburger menu */
            <div style={{ position: 'relative' }}>
              <button onClick={() => setMapMenuOpen(!mapMenuOpen)} style={{
                padding: '6px 10px', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer',
                background: mapMenuOpen ? accent + '20' : 'transparent', color: mapMenuOpen ? accent : textMuted,
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
              }}>
                {mapMenuOpen ? <X size={16} /> : <Menu size={16} />}
              </button>

              {/* Hamburger dropdown */}
              {mapMenuOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 1000,
                  backgroundColor: darkMode ? 'rgba(17,34,64,0.98)' : 'rgba(255,255,255,0.98)',
                  borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)',
                  padding: '8px 0', minWidth: 200, border: `1px solid ${borderColor}`,
                }}>
                  {/* Filters toggle */}
                  <button onClick={() => { setShowFilters(!showFilters); }} style={{
                    width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13,
                    color: text,
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Filter size={14} color={showFilters ? accent : textMuted} />
                      Filters
                    </span>
                    <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: showFilters ? accent : (darkMode ? '#333' : '#ddd') }} />
                  </button>

                  {/* Stats toggle */}
                  <button onClick={() => { setShowStats(!showStats); }} style={{
                    width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13,
                    color: text,
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Activity size={14} color={showStats ? accent : textMuted} />
                      Stats Bar
                    </span>
                    <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: showStats ? accent : (darkMode ? '#333' : '#ddd') }} />
                  </button>

                  {/* Notes toggle */}
                  <button onClick={() => setShowNotes(!showNotes)} style={{
                    width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13,
                    color: text,
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MapPin size={14} color={showNotes ? '#FF9800' : textMuted} />
                      Notes {mapNotes.length > 0 && <span style={{ background: '#FF9800', color: '#fff', padding: '1px 5px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{mapNotes.length}</span>}
                    </span>
                    <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: showNotes ? '#FF9800' : (darkMode ? '#333' : '#ddd') }} />
                  </button>

                  {/* Add Note */}
                  {showNotes && (
                    <button onClick={() => { setIsAddingNote(!isAddingNote); setMapMenuOpen(false); }} style={{
                      width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13,
                      color: isAddingNote ? '#FF5722' : text,
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MapPin size={14} color={isAddingNote ? '#FF5722' : textMuted} />
                        {isAddingNote ? 'Cancel Note Placement' : 'Add Note'}
                      </span>
                    </button>
                  )}

                  {/* Admin toggle */}
                  {isAdmin && (
                    <button onClick={() => { setAdminMode(!adminMode); if (adminMode) { setSelectedItems([]); } else { clearSelections(); } }} style={{
                      width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13,
                      color: text,
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Shield size={14} color={adminMode ? '#c850c0' : textMuted} />
                        Admin Mode
                        {adminMode && selectedItems.length > 0 && (
                          <span style={{ background: '#FFD700', color: '#000', padding: '1px 5px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{selectedItems.length}</span>
                        )}
                      </span>
                      <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: adminMode ? '#c850c0' : (darkMode ? '#333' : '#ddd') }} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Desktop: inline buttons */
            <>
              {/* Filter toggle */}
              <button onClick={() => setShowFilters(!showFilters)} style={{
                padding: '6px 10px', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer',
                background: showFilters ? accent + '20' : 'transparent', color: showFilters ? accent : textMuted,
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
              }}>
                <Filter size={14} /> Filters
              </button>
              {/* Admin Mode toggle in header */}
              {isAdmin && (
                <button
                  onClick={() => { setAdminMode(!adminMode); if (adminMode) { setSelectedItems([]); } else { clearSelections(); } }}
                  style={{
                    padding: '6px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: adminMode ? '#c850c0' : (darkMode ? '#1e3a5f' : '#e2e8f0'),
                    color: adminMode ? '#fff' : textMuted,
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Shield size={14} />
                  {adminMode ? 'Exit Admin' : 'Admin'}
                  {adminMode && selectedItems.length > 0 && (
                    <span style={{ background: '#FFD700', color: '#000', padding: '1px 5px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{selectedItems.length}</span>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div style={{
          padding: '10px 20px', borderBottom: `1px solid ${borderColor}`,
          background: darkMode ? '#0f1f38' : '#f1f5f9',
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', flexShrink: 0,
        }}>
          {/* Preset buttons */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'construction', label: 'Construction' },
              { key: 'pulling', label: 'Pulling' },
              { key: 'splicing', label: 'Splicing' },
            ].map(p => (
              <button key={p.key} onClick={() => applyPreset(p.key)} style={{
                padding: '4px 12px', borderRadius: 16, border: `1px solid ${activePreset === p.key ? accent : borderColor}`, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: activePreset === p.key ? accent : 'transparent', color: activePreset === p.key ? '#fff' : textMuted,
              }}>
                {p.label}
              </button>
            ))}
            {isAdmin && (
              <button onClick={handleSavePreset} disabled={savingPreset} title={`Save current settings as "${activePreset}" default`} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${borderColor}`, cursor: savingPreset ? 'not-allowed' : 'pointer',
                fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: 'transparent', color: textMuted, opacity: savingPreset ? 0.5 : 1,
              }}>
                <Save size={12} /> Save
              </button>
            )}
          </div>

          {/* Divider */}
          <div style={{ width: 1, height: 20, background: borderColor }} />

          {/* View mode toggle */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setViewMode('construction')} style={{
              padding: '4px 12px', borderRadius: 16, border: `1px solid ${borderColor}`, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: viewMode === 'construction' ? accent : 'transparent', color: viewMode === 'construction' ? '#fff' : textMuted,
            }}>
              Construction
            </button>
            <button onClick={() => setViewMode('vitruvi')} style={{
              padding: '4px 12px', borderRadius: 16, border: `1px solid ${borderColor}`, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: viewMode === 'vitruvi' ? '#9C27B0' : 'transparent', color: viewMode === 'vitruvi' ? '#fff' : textMuted,
            }}>
              Aspen/Billed
            </button>
          </div>

          {/* Divider */}
          <div style={{ width: 1, height: 24, background: borderColor }} />

          {/* Phase selector */}
          <div style={{ display: 'flex', gap: 4 }}>
            {['boring', 'pulling', 'splicing'].map(phase => (
              <button key={phase} onClick={() => setFilterPhase(phase)} style={{
                padding: '4px 12px', borderRadius: 16, border: `1px solid ${borderColor}`, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: filterPhase === phase ? accent : 'transparent', color: filterPhase === phase ? '#fff' : textMuted,
              }}>
                {phase === 'boring' ? '🚧' : phase === 'pulling' ? '🚛' : '⚡'} {phase.charAt(0).toUpperCase() + phase.slice(1)}
              </button>
            ))}
          </div>

          {/* Section filter */}
          <select value={filterSection} onChange={e => setFilterSection(e.target.value)} style={{
            padding: '4px 8px', borderRadius: 6, border: `1px solid ${borderColor}`, fontSize: 12,
            background: darkMode ? '#112240' : '#fff', color: text,
          }}>
            <option value="all">All Sections</option>
            {availableSections.length > 0
              ? availableSections.map(s => <option key={s.section} value={s.section}>{s.section} ({s.count})</option>)
              : sections.map(s => <option key={s} value={s}>Section {s}</option>)
            }
          </select>

          {/* Status filter */}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{
            padding: '4px 8px', borderRadius: 6, border: `1px solid ${borderColor}`, fontSize: 12,
            background: darkMode ? '#112240' : '#fff', color: text,
          }}>
            <option value="all">All Statuses</option>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="Complete">Complete</option>
            <option value="QC Approved">QC Approved</option>
            <option value="Issue">Issue</option>
          </select>

          {/* Crew / Assigned-To filter (admin only) */}
          {isAdmin && availableCrews.length > 0 && (
            <select value={filterCrew} onChange={e => setFilterCrew(e.target.value)} style={{
              padding: '4px 8px', borderRadius: 6, border: `1px solid ${filterCrew !== 'all' ? accent : borderColor}`, fontSize: 12,
              background: darkMode ? '#112240' : '#fff', color: text,
            }}>
              <option value="all">All Crews</option>
              {availableCrews.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          )}

          {/* Show all Vitruvi data toggle (admin only) */}
          {isAdmin && (
            <button onClick={() => setShowAllVitruvi(prev => !prev)} style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${showAllVitruvi ? accent : borderColor}`,
              background: showAllVitruvi ? accent : 'transparent',
              color: showAllVitruvi ? '#fff' : textMuted,
            }}>
              {showAllVitruvi ? 'All Vitruvi ✓' : 'All Vitruvi'}
            </button>
          )}

          {/* Search — full width on mobile */}
          <div style={{ position: 'relative', flex: 1, minWidth: isMobile ? '100%' : 150, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: 7, color: textMuted }} />
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search segments..." style={{
                width: '100%', padding: '4px 8px 4px 28px', borderRadius: 6, border: `1px solid ${searchMatchIds ? accent : borderColor}`, fontSize: 12,
                background: darkMode ? '#112240' : '#fff', color: text, outline: 'none',
              }} />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: 6, top: 5, background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 0 }}>
                  <X size={14} />
                </button>
              )}
            </div>
            {searchMatchIds && (
              <span style={{ fontSize: 11, fontWeight: 600, color: accent, whiteSpace: 'nowrap' }}>
                {searchMatchIds.size} of {filteredSegments.length}
              </span>
            )}
          </div>

          {/* Layer Toggles — collapsible on mobile */}
          {isMobile ? (
            <button onClick={() => setShowLayerToggles(!showLayerToggles)} style={{
              padding: '4px 12px', borderRadius: 16, border: `1px solid ${showLayerToggles ? accent : borderColor}`,
              cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: showLayerToggles ? `${accent}20` : 'transparent',
              color: showLayerToggles ? accent : textMuted,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Eye size={12} /> Layers
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 4, borderLeft: `1px solid ${borderColor}`, paddingLeft: 10 }}>
              <button onClick={() => setShowHandholes(!showHandholes)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showHandholes ? '#2196F3' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showHandholes ? '#2196F320' : 'transparent', color: showHandholes ? '#2196F3' : textMuted,
              }}>
                <MapPin size={12} /> HH
              </button>
              <button onClick={() => setShowFlowerpots(!showFlowerpots)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showFlowerpots ? '#9C27B0' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showFlowerpots ? '#9C27B020' : 'transparent', color: showFlowerpots ? '#9C27B0' : textMuted,
              }}>
                <Flower2 size={12} /> FP
              </button>
              <button onClick={() => setShowPedestals(!showPedestals)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showPedestals ? '#00897B' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showPedestals ? '#00897B20' : 'transparent', color: showPedestals ? '#00897B' : textMuted,
              }}>
                <MapPin size={12} /> Ped
              </button>
              <button onClick={() => setShowCabinets(!showCabinets)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showCabinets ? '#B71C1C' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showCabinets ? '#B71C1C20' : 'transparent', color: showCabinets ? '#B71C1C' : textMuted,
              }}>
                <HardHat size={12} /> Cab
              </button>
              <button onClick={() => setShowSplicePoints(!showSplicePoints)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showSplicePoints ? '#E91E63' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showSplicePoints ? '#E91E6320' : 'transparent', color: showSplicePoints ? '#E91E63' : textMuted,
              }}>
                <Zap size={12} /> Splice
              </button>
              <button onClick={() => setShowAerial(!showAerial)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showAerial ? '#4682B4' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showAerial ? '#4682B420' : 'transparent', color: showAerial ? '#4682B4' : textMuted,
              }}>
                <Cable size={12} /> Aerial
              </button>
              <button onClick={() => setShowFats(!showFats)} style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${showFats ? '#FF9800' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: showFats ? '#FF980020' : 'transparent', color: showFats ? '#FF9800' : textMuted,
              }}>
                <Zap size={12} /> FAT
              </button>
              <button onClick={() => setColorblindMode(!colorblindMode)} title="Colorblind-friendly palette (no red/green)" style={{
                padding: '4px 10px', borderRadius: 16, border: `1px solid ${colorblindMode ? '#CC79A7' : borderColor}`,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                background: colorblindMode ? '#CC79A720' : 'transparent', color: colorblindMode ? '#CC79A7' : textMuted,
                marginLeft: 4,
              }}>
                <Eye size={12} /> CB
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mobile Layer Toggles — expandable panel */}
      {isMobile && showFilters && showLayerToggles && (
        <div style={{
          padding: '8px 20px', borderBottom: `1px solid ${borderColor}`,
          background: darkMode ? '#0f1f38' : '#f1f5f9',
          display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          <button onClick={() => setShowHandholes(!showHandholes)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showHandholes ? '#2196F3' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showHandholes ? '#2196F320' : 'transparent', color: showHandholes ? '#2196F3' : textMuted,
          }}>
            <MapPin size={12} /> HH
          </button>
          <button onClick={() => setShowFlowerpots(!showFlowerpots)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showFlowerpots ? '#9C27B0' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showFlowerpots ? '#9C27B020' : 'transparent', color: showFlowerpots ? '#9C27B0' : textMuted,
          }}>
            <Flower2 size={12} /> FP
          </button>
          <button onClick={() => setShowPedestals(!showPedestals)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showPedestals ? '#00897B' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showPedestals ? '#00897B20' : 'transparent', color: showPedestals ? '#00897B' : textMuted,
          }}>
            <MapPin size={12} /> Ped
          </button>
          <button onClick={() => setShowCabinets(!showCabinets)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showCabinets ? '#B71C1C' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showCabinets ? '#B71C1C20' : 'transparent', color: showCabinets ? '#B71C1C' : textMuted,
          }}>
            <HardHat size={12} /> Cab
          </button>
          <button onClick={() => setShowSplicePoints(!showSplicePoints)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showSplicePoints ? '#E91E63' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showSplicePoints ? '#E91E6320' : 'transparent', color: showSplicePoints ? '#E91E63' : textMuted,
          }}>
            <Zap size={12} /> Splice
          </button>
          <button onClick={() => setShowAerial(!showAerial)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showAerial ? '#4682B4' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showAerial ? '#4682B420' : 'transparent', color: showAerial ? '#4682B4' : textMuted,
          }}>
            <Cable size={12} /> Aerial
          </button>
          <button onClick={() => setShowFats(!showFats)} style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${showFats ? '#FF9800' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: showFats ? '#FF980020' : 'transparent', color: showFats ? '#FF9800' : textMuted,
          }}>
            <Zap size={12} /> FAT
          </button>
          <button onClick={() => setColorblindMode(!colorblindMode)} title="Colorblind-friendly palette (no red/green)" style={{
            padding: '4px 10px', borderRadius: 16, border: `1px solid ${colorblindMode ? '#CC79A7' : borderColor}`,
            cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            background: colorblindMode ? '#CC79A720' : 'transparent', color: colorblindMode ? '#CC79A7' : textMuted,
          }}>
            <Eye size={12} /> CB
          </button>
        </div>
      )}

      {/* Stats Bar */}
      {showStats && <div style={{
        padding: '8px 20px', borderBottom: `1px solid ${borderColor}`,
        display: 'flex', gap: 16, overflowX: 'auto', flexShrink: 0,
        background: darkMode ? '#0f1f38' : '#f1f5f9',
      }}>
        {[
          { label: 'Segments', value: stats.total, color: text },
          { label: 'Footage', value: `${stats.totalFootage.toLocaleString()} LF`, color: accent },
          { label: 'QC Approved', value: stats.approved, color: (colorblindMode ? STATUS_COLORS_CB : STATUS_COLORS).QC_APPROVED },
          { label: 'Complete', value: stats.complete, color: (colorblindMode ? STATUS_COLORS_CB : STATUS_COLORS).COMPLETE },
          { label: 'In Progress', value: stats.inProgress, color: (colorblindMode ? STATUS_COLORS_CB : STATUS_COLORS).IN_PROGRESS },
          ...(showHandholes && stats.handholesCount > 0 ? [
            { label: 'Handholes', value: stats.handholesCount, color: '#2196F3' },
          ] : []),
          ...(showFlowerpots && stats.flowerpotCount > 0 ? [
            { label: 'Flowerpots', value: stats.flowerpotCount, color: '#9C27B0' },
          ] : []),
          ...(showPedestals && stats.pedestalCount > 0 ? [
            { label: 'Pedestals', value: stats.pedestalCount, color: '#00897B' },
          ] : []),
          ...(showCabinets && stats.cabinetCount > 0 ? [
            { label: 'Cabinets', value: stats.cabinetCount, color: '#B71C1C' },
          ] : []),
          ...(showFats && stats.fatCount > 0 ? [
            { label: 'FATs', value: stats.fatCount, color: '#FF9800' },
          ] : []),
          ...(stats.groundRodCount > 0 ? [
            { label: 'Ground Rods', value: stats.groundRodCount, color: '#795548' },
          ] : []),
          ...(showSplicePoints ? [
            { label: '1×4 Splices', value: stats.splice1x4, color: '#E91E63' },
            { label: '1×8 Hubs', value: stats.splice2x8, color: '#3F51B5' },
          ] : []),
        ].map(stat => (
          <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: stat.color }}>{stat.value}</span>
            <span style={{ fontSize: 11, color: textMuted }}>{stat.label}</span>
          </div>
        ))}
      </div>}

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Map View */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <div style={{ position: 'relative', height: '100%' }}>
              <ProjectGoogleMap
                segments={filteredSegments}
                splicePoints={visibleSplicePoints}
                handholes={visibleHandholes}
                flowerpots={visibleFlowerpots}
                pedestals={visiblePedestals}
                cabinets={visibleCabinets}
                fats={visibleFats}
                selectedSegment={selectedSegment}
                selectedSplice={selectedSplice}
                onSelectSegment={adminMode ? handleAdminSelectSegment : handleSelectSegment}
                onSelectSplice={adminMode ? (sp) => handleAdminSelectDevice(sp, 'splice') : handleSelectSplice}
                onSelectHandhole={adminMode ? (hh) => handleAdminSelectDevice(hh, 'handhole') : handleSelectHandhole}
                onSelectFlowerpot={adminMode ? (fp) => handleAdminSelectDevice(fp, 'flowerpot') : handleSelectFlowerpot}
                onSelectPedestal={adminMode ? (ped) => handleAdminSelectDevice(ped, 'pedestal') : handleSelectPedestal}
                onSelectCabinet={adminMode ? (cab) => handleAdminSelectDevice(cab, 'cabinet') : handleSelectCabinet}
                filterPhase={filterPhase}
                darkMode={darkMode}
                showFlowerpots={showFlowerpots}
                showSplicePoints={showSplicePoints}
                showHandholes={showHandholes}
                showPedestals={showPedestals}
                showCabinets={showCabinets}
                showAerial={showAerial}
                showFats={showFats}
                adminMode={adminMode}
                adminSelectedSegmentIds={adminSelectedSegmentIds}
                adminSelectedDeviceIds={adminSelectedDeviceIds}
                projectId={currentProjectId}
                drawRectMode={drawRectMode}
                searchMatchIds={searchMatchIds}
                viewMode={viewMode}
                colorblindMode={colorblindMode}
                mapNotes={mapNotes}
                showNotes={showNotes}
                isAddingNote={isAddingNote}
                onAddNoteToggle={setIsAddingNote}
                onNotesChanged={async () => {
                  try {
                    const sectionParam = filterSection !== 'all' ? filterSection : undefined;
                    const notesData = await loadMapNotes(currentProjectId, sectionParam);
                    setMapNotes(notesData || []);
                  } catch (e) { console.warn('[ProjectMap] Notes refresh:', e); }
                }}
                loggedInUser={user}
                section={filterSection !== 'all' ? filterSection : null}
                allDevices={[...(visibleHandholes || []), ...(visibleFlowerpots || []), ...(visibleSplicePoints || []), ...(visiblePedestals || []), ...(visibleCabinets || [])]}
                isAdmin={isAdmin}
                isMobile={isMobile}
                gpsOn={gpsOn}
                onGpsToggle={setGpsOn}
                onRectangleSelect={(bounds) => {
                  const inBounds = (lat, lng) => lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
                  const newItems = [];
                  // Select segments whose start OR end falls within the rectangle
                  filteredSegments.forEach(seg => {
                    const sLat = parseFloat(seg.gps_start_lat);
                    const sLng = parseFloat(seg.gps_start_lng);
                    const eLat = parseFloat(seg.gps_end_lat);
                    const eLng = parseFloat(seg.gps_end_lng);
                    if ((!isNaN(sLat) && !isNaN(sLng) && inBounds(sLat, sLng)) || (!isNaN(eLat) && !isNaN(eLng) && inBounds(eLat, eLng))) {
                      if (!selectedItems.find(i => i.type === 'segment' && i.id === seg.segment_id)) {
                        newItems.push({ type: 'segment', id: seg.segment_id, data: seg });
                      }
                    }
                  });
                  // Select devices (handholes, flowerpots, splices, pedestals, cabinets) within the rectangle
                  const allDevices = [
                    ...(visibleHandholes || []).map(d => ({ ...d, _did: d.device_id || d.id })),
                    ...(visibleFlowerpots || []).map(d => ({ ...d, _did: d.device_id })),
                    ...(visibleSplicePoints || []).map(d => ({ ...d, _did: d.device_id || d.splice_id })),
                    ...(visiblePedestals || []).map(d => ({ ...d, _did: d.device_id })),
                    ...(visibleCabinets || []).map(d => ({ ...d, _did: d.device_id })),
                  ];
                  allDevices.forEach(dev => {
                    const lat = parseFloat(dev.gps_lat);
                    const lng = parseFloat(dev.gps_lng);
                    if (!isNaN(lat) && !isNaN(lng) && inBounds(lat, lng)) {
                      if (!selectedItems.find(i => i.type === 'device' && i.id === dev._did)) {
                        newItems.push({ type: 'device', id: dev._did, data: dev });
                      }
                    }
                  });
                  if (newItems.length > 0) {
                    setSelectedItems(prev => [...prev, ...newItems]);
                  }
                  setDrawRectMode(false);
                }}
              />
              {/* CrewTracker removed — GPS controlled from Settings */}
            </div>
        </div>

        {/* Admin Mode Toggle — in header bar, not floating on map */}

        {/* Admin Batch Panel - Desktop */}
        {adminMode && isAdmin && !isMobile && (
          <div style={{
            width: 380, flexShrink: 0, borderLeft: `1px solid ${borderColor}`,
            overflow: 'hidden',
            background: darkMode ? '#0d1b2a' : '#ffffff',
            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
            boxSizing: 'border-box',
          }}>
            <AdminBatchPanel
              selectedItems={selectedItems} setSelectedItems={setSelectedItems} darkMode={darkMode}
              adminPhaseStatus={adminPhaseStatus} setAdminPhaseStatus={setAdminPhaseStatus}
              adminDeviceStatus={adminDeviceStatus} setAdminDeviceStatus={setAdminDeviceStatus}
              adminBillingStatus={adminBillingStatus} setAdminBillingStatus={setAdminBillingStatus}
              adminInvoiceId={adminInvoiceId} setAdminInvoiceId={setAdminInvoiceId}
              adminApplyPhases={adminApplyPhases} setAdminApplyPhases={setAdminApplyPhases}
              adminSaving={adminSaving}
              onApplyStatus={handleApplyBatchStatus}
              onApplyBilling={handleApplyBatchBilling}
              onApplyLyt={handleApplyBatchLyt}
              onApplySection={handleApplyBatchSection}
              onApplyProject={handleApplyBatchProject}
              onSelectInvoice={handleSelectInvoice}
              onSelectAll={handleAdminSelectAll}
              onClearSelection={() => setSelectedItems([])}
              onClose={() => { setAdminMode(false); setSelectedItems([]); }}
              existingSections={sections}
              allProjectsList={allProjectsList}
              currentProjectId={currentProjectId}
              paymentInvoices={paymentInvoices}
              selectedInvoiceId={selectedInvoiceId}
              setSelectedInvoiceId={setSelectedInvoiceId}
              invoiceFetching={invoiceFetching}
              onRefreshInvoices={handleRefreshInvoices}
              segments={allSegments}
              allDevices={[...(splicePoints || []), ...(handholes || []), ...(flowerpots || []), ...(pedestals || []), ...(cabinets || []), ...(fats || [])]}
              assignments={projectAssignments}
              drawRectMode={drawRectMode}
              setDrawRectMode={setDrawRectMode}
              onQuickStatus={handleQuickStatus}
              onAssign={async (segIds, workType, contractor, email) => {
                const result = await bulkAssignSegments(segIds, workType, contractor, currentProjectId, email);
                if (result.success || result.updated > 0) {
                  setSelectedItems([]);
                  // Trigger data refresh
                  setLastRefresh(Date.now());
                }
                return result;
              }}
            />
          </div>
        )}

        {/* Detail Panel - Desktop (Segment, Splice, Handhole, Flowerpot, Pedestal, or Cabinet) */}
        {!adminMode && (selectedSegment || selectedSplice || selectedHandhole || selectedFlowerpot || selectedPedestal || selectedCabinet) && !isMobile && (
          <div style={{
            width: 380, flexShrink: 0, borderLeft: `1px solid ${borderColor}`,
            overflow: 'hidden',
            background: darkMode ? '#0d1b2a' : '#ffffff',
            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
            boxSizing: 'border-box',
          }}>
            {selectedSegment && (
              <SegmentDetailPanel segment={selectedSegment} darkMode={darkMode} onClose={() => setSelectedSegment(null)} isAdmin={isAdmin} user={user} locates={locates} onCreateLocate={async (data) => { const ok = await createLocate(data); if (ok) { const updated = await loadLocates(currentProjectId || projectInfo?.project_id); setLocates(updated); } }} onVerifyLocate={async (locateId, verifiedBy) => { const ok = await verifyLocate(locateId, verifiedBy); if (ok) { const updated = await loadLocates(currentProjectId || projectInfo?.project_id); setLocates(updated); } }} damageReports={damageReports} onCreateDamageReport={async (data) => { const reportId = await createDamageReport(data); if (reportId) { setDamageReports(prev => [...prev, { ...data, report_id: reportId, status: 'Open', timestamp: new Date().toISOString() }]); } }} onSegmentUpdate={(segId, field, value) => { setAllSegments(prev => prev.map(s => s.segment_id === segId ? { ...s, [field]: value } : s)); setSelectedSegment(prev => prev && prev.segment_id === segId ? { ...prev, [field]: value } : prev); }} showToast={showToast} colorblindMode={colorblindMode} />
            )}
            {selectedSplice && (
              <SplicePointDetailPanel splicePoint={selectedSplice} darkMode={darkMode} onClose={() => setSelectedSplice(null)} user={user} onSpliceUpdate={handleSpliceUpdate} />
            )}
            {selectedHandhole && (
              <HandholeDetailPanel handhole={selectedHandhole} segments={allSegments} darkMode={darkMode} onClose={() => setSelectedHandhole(null)} />
            )}
            {selectedFlowerpot && (
              <FlowerpotDetailPanel flowerpot={selectedFlowerpot} segments={allSegments} darkMode={darkMode} user={user} onClose={() => setSelectedFlowerpot(null)} onFlowerpotUpdate={(devId, field, value) => { setSelectedFlowerpot(prev => prev && prev.device_id === devId ? { ...prev, [field]: value } : prev); }} />
            )}
            {selectedPedestal && (
              <DeviceDetailPanel device={selectedPedestal} deviceType="pedestal" segments={allSegments} darkMode={darkMode} onClose={() => setSelectedPedestal(null)} />
            )}
            {selectedCabinet && (
              <DeviceDetailPanel device={selectedCabinet} deviceType="cabinet" segments={allSegments} darkMode={darkMode} onClose={() => setSelectedCabinet(null)} />
            )}
          </div>
        )}

        {/* Detail Panel - Mobile Bottom Sheet */}
        {(selectedSegment || selectedSplice || selectedHandhole || selectedFlowerpot || selectedPedestal || selectedCabinet) && isMobile && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            maxHeight: '70vh', borderTop: `1px solid ${borderColor}`,
            borderRadius: '16px 16px 0 0', overflow: 'hidden',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
            background: darkMode ? '#0d1b2a' : '#ffffff',
            zIndex: 20,
          }}>
            {/* Drag handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: borderColor }} />
            </div>
            <div style={{ overflow: 'auto', maxHeight: 'calc(70vh - 20px)' }}>
              {selectedSegment && (
                <SegmentDetailPanel segment={selectedSegment} darkMode={darkMode} onClose={() => setSelectedSegment(null)} isAdmin={isAdmin} user={user} locates={locates} onCreateLocate={async (data) => { const ok = await createLocate(data); if (ok) { const updated = await loadLocates(currentProjectId || projectInfo?.project_id); setLocates(updated); } }} onVerifyLocate={async (locateId, verifiedBy) => { const ok = await verifyLocate(locateId, verifiedBy); if (ok) { const updated = await loadLocates(currentProjectId || projectInfo?.project_id); setLocates(updated); } }} damageReports={damageReports} onCreateDamageReport={async (data) => { const reportId = await createDamageReport(data); if (reportId) { setDamageReports(prev => [...prev, { ...data, report_id: reportId, status: 'Open', timestamp: new Date().toISOString() }]); } }} onSegmentUpdate={(segId, field, value) => { setAllSegments(prev => prev.map(s => s.segment_id === segId ? { ...s, [field]: value } : s)); setSelectedSegment(prev => prev && prev.segment_id === segId ? { ...prev, [field]: value } : prev); }} showToast={showToast} colorblindMode={colorblindMode} />
              )}
              {selectedSplice && (
                <SplicePointDetailPanel splicePoint={selectedSplice} darkMode={darkMode} onClose={() => setSelectedSplice(null)} user={user} onSpliceUpdate={handleSpliceUpdate} />
              )}
              {selectedHandhole && (
                <HandholeDetailPanel handhole={selectedHandhole} segments={allSegments} darkMode={darkMode} onClose={() => setSelectedHandhole(null)} />
              )}
              {selectedFlowerpot && (
                <FlowerpotDetailPanel flowerpot={selectedFlowerpot} segments={allSegments} darkMode={darkMode} user={user} onClose={() => setSelectedFlowerpot(null)} onFlowerpotUpdate={(devId, field, value) => { setSelectedFlowerpot(prev => prev && prev.device_id === devId ? { ...prev, [field]: value } : prev); }} />
              )}
              {selectedPedestal && (
                <DeviceDetailPanel device={selectedPedestal} deviceType="pedestal" segments={allSegments} darkMode={darkMode} onClose={() => setSelectedPedestal(null)} />
              )}
              {selectedCabinet && (
                <DeviceDetailPanel device={selectedCabinet} deviceType="cabinet" segments={allSegments} darkMode={darkMode} onClose={() => setSelectedCabinet(null)} />
              )}
            </div>
          </div>
        )}

        {/* Mobile Admin Status Bar — floating bottom bar when items selected */}
        {adminMode && isAdmin && isMobile && selectedItems.length > 0 && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: darkMode ? 'rgba(13,27,42,0.95)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            borderTop: `1px solid ${borderColor}`,
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
            zIndex: 25, padding: '12px 16px',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          }}>
            {/* Header: count + clear */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: darkMode ? '#fff' : '#1a1a2e' }}>
                {selectedItems.length} selected
              </span>
              <button onClick={() => setSelectedItems([])} style={{
                background: 'none', border: 'none', color: darkMode ? '#9ca3af' : '#6b7280',
                fontSize: '0.85rem', cursor: 'pointer', padding: '4px 8px',
              }}>Clear</button>
            </div>
            {/* Status buttons row */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { label: 'In Progress', status: 'In Progress', color: '#FFB800' },
                { label: 'Complete', status: 'Complete', color: '#4CAF50' },
                { label: 'QC Approved', status: 'QC Approved', color: '#2196F3' },
                { label: 'Issue', status: 'Issue', color: '#FF5722' },
              ].map(btn => (
                <button key={btn.status} disabled={adminSaving} onClick={() => handleQuickStatus(btn.status)} style={{
                  flex: 1, padding: '10px 4px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: btn.color, color: '#fff', fontWeight: 600, fontSize: '0.75rem',
                  opacity: adminSaving ? 0.5 : 1,
                }}>
                  {adminSaving ? '...' : btn.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hidden version */}
      <div onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }} style={{ position: 'fixed', bottom: 4, right: 8, fontSize: 9, color: showVersion ? (darkMode ? '#fff' : '#333') : 'transparent', opacity: showVersion ? 0.5 : 1, userSelect: 'none', cursor: 'default' }}>ProjectMapPage v4.3.0</div>
      <ToastContainer />
    </div>
  );
}

export default ProjectMapPage;
